/**
 * COLLECTION PAGE
 *
 * The main collection management interface where users can:
 * - View, organize, and manage their nail art collection
 * - Filter by item type (polish, plates, tools, transfers, embellishments, powders, acrylics)
 * - Apply advanced filters (brand, color family, release info, storage location, etc.)
 * - Search by name, brand, or tags
 * - Sort items in multiple ways (brand, date added, name, etc.)
 * - Switch between grid and list views
 * - Select multiple items for batch operations (merge, delete, archive)
 * - Color-search polishes using visual color picker
 * - Tag items with detailed metadata (for polishes and plates)
 *
 * Data Flow:
 * 1. Load items from Firebase on mount (getUserItems from db.ts)
 * 2. Apply filters and search to create displayed items
 * 3. Paginate results (24/48/96 per page - multiples of common grid columns)
 * 4. Render cards/list items with click handlers to open detail modals
 *
 * URL Parameters:
 * - ?type=polish: Pre-filter to show only polish items
 * - Maintains filter state in URL for bookmarking/sharing
 *
 * Key Features:
 * - Archive system: separate "in collection" vs "destashed" items
 * - Selection mode for bulk operations
 * - Color-based search for finding similar polish shades
 * - Comprehensive release tracking (event, collection, core, limited edition)
 * - Mobile-responsive filters with glassmorphic styling
 */

import { useState, useEffect, useRef, type ReactElement } from 'react';
import { useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { getUserItems } from '../firebase/db';
import { getCurrentUser } from '../firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, updateDoc, getDoc, deleteField, collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import type { CollectionItem } from '../types';
import type { BrandCategory } from '../types/brandCategories';
import { getItemPhoto } from '../utils/getItemPhoto';
import {
  Download, CheckSquare, Square, Grid, List, X,
  SlidersHorizontal, Search, ChevronDown, ChevronUp, Plus
} from 'lucide-react';
import { AppIcons } from '../constants/icons';
import { CategoryColors } from '../constants/categoryColors';
import PlateCard from '../components/plates/PlateCard';
import DecalCard from '../components/decals/DecalCard';
import CollectionDetailModals from '../components/collection/CollectionDetailModals';
import CollectionBulkActions from '../components/collection/CollectionBulkActions';
import CollectionAddModals from '../components/collection/CollectionAddModals';
import PolishCard from '../components/polish/PolishCard';
import EmbellishmentCard from '../components/embellishments/EmbellishmentCard';
import ToolCard from '../components/tools/ToolCard';
import CuticleCareCard from '../components/cuticleCare/CuticleCareCard';
import BuffingPowderCard from '../components/powders/BuffingPowderCard';
import AcrylicCard from '../components/acrylics/AcrylicCard';
import MergeItemsModal from '../components/MergeItemsModal';
import ColorSearchFilter from '../components/ColorSearchFilter';
import PageVisibilityBanner from '../components/PageVisibilityBanner';

const Collection = (): ReactElement => {
  // URL Parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Ref to skip URL sync after adding an item (prevents flash to 'all')
  const skipNextUrlSync = useRef(false);
  
  // Ref to track last processed URL params (prevents re-processing on allItems changes)
  const lastProcessedParams = useRef<string>('');
  
  // State
  const [allItems, setAllItems] = useState<CollectionItem[]>([]);
  const [brandCategories, setBrandCategories] = useState<Record<string, BrandCategory>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  
  const [loading, setLoading] = useState<boolean>(true);
  
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [mainFilter, setMainFilter] = useState<string>('all');
  const [polishSubFilter, setPolishSubFilter] = useState<string>('all');
  const [plateSubFilter, setPlateSubFilter] = useState<string>('all');
  const [transferSubFilter, setTransferSubFilter] = useState<string>('all');
  const [embellishmentSubFilter, setEmbellishmentSubFilter] = useState<string>('all');
  
  // Advanced filters
  const [sortBy, setSortBy] = useState<string>('brand-name');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [storageFilter, setStorageFilter] = useState<string>('all');
  const [quantityFilter, setQuantityFilter] = useState<string>('any');
  const [tagFilter, setTagFilter] = useState<string>('');
  
  // NEW: Additional filters
  const [colorFamilyFilter, setColorFamilyFilter] = useState<string>('all');
  const [completenessFilter, setCompletenessFilter] = useState<string>('all');
  const [searchColor, setSearchColor] = useState<string | null>(null);
  const [colorSimilarity, setColorSimilarity] = useState<number>(80);
  
  // NEW: Archive filter (replaces collection/event filters)
  const [archiveFilter, setArchiveFilter] = useState<string>('in-collection');
  
  // NEW: Hide wishlist items toggle (persisted in localStorage)
  const [hideWishlistItems, setHideWishlistItems] = useState<boolean>(() => {
    const saved = localStorage.getItem('collection-hide-wishlist');
    return saved === 'true';
  });
  
  // Persist hideWishlistItems to localStorage
  useEffect(() => {
    localStorage.setItem('collection-hide-wishlist', hideWishlistItems.toString());
  }, [hideWishlistItems]);
  
  // NEW: Release system filters
  const [releaseTypeFilter, setReleaseTypeFilter] = useState<string>('all');
  const [eventSourceFilter, setEventSourceFilter] = useState<string>('all');
  const [exclusiveRetailerFilter, setExclusiveRetailerFilter] = useState<string>('all');
  const [boxNameFilter, setBoxNameFilter] = useState<string>('all');
  const [groupNameFilter, setGroupNameFilter] = useState<string>('all');
  const [charityNameFilter, setCharityNameFilter] = useState<string>('all');
  const [conventionNameFilter, setConventionNameFilter] = useState<string>('all');
  const [collabWithFilter, setCollabWithFilter] = useState<string>('all');
  const [collectionNameFilter, setCollectionNameFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [seasonFilter, setSeasonFilter] = useState<string>('all');
  
  // NEW: Collection/Event filter (for filtering by collectionName or eventTheme within selected brand)
  const [collectionEventFilter, setCollectionEventFilter] = useState<string>('all');
  
  const [selectedPolish, setSelectedPolish] = useState<CollectionItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [addItemCategory, setAddItemCategory] = useState<string | null>(null);

  // Deep-link entry point from Marketplace's "sell an item" hint — arriving
  // with `?add=1` auto-opens the item type picker so the tester lands one
  // click closer to their goal instead of having to hunt for the Add button.
  useEffect(() => {
    if (searchParams.get('add') === '1') {
      setShowItemPicker(true);
      // Strip the param so a refresh doesn't keep re-opening the picker.
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // SELECTION MODE STATE
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expandedFilterSections, setExpandedFilterSections] = useState<Set<string>>(new Set(['categories']));
  
  // Toast notification state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'info' | 'warning';
    undoAction?: () => void;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // User profile for visibility banner
  const [username, setUsername] = useState<string>('');
  const [isCollectionPublic, setIsCollectionPublic] = useState<boolean>(false);
  
  // Load data when auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        loadUserItems(user.uid);
        
        // Fetch user profile for visibility banner
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUsername(data.username || data.profile?.username || '');
            setIsCollectionPublic(data.privacy?.showCollection ?? data.isCollectionPublic ?? false);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setAllItems([]);
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  const loadUserItems = async (userId: string) => {
    try {
      setLoading(true);
      const userItems = await getUserItems(userId);
      setAllItems(userItems);
      
      // Load brand categories
      try {
        const categoriesRef = collection(db, 'users', userId, 'brandCategories');
        const categoriesSnapshot = await getDocs(categoriesRef);
        const categories: Record<string, BrandCategory> = {};
        categoriesSnapshot.forEach((doc) => {
          const data = doc.data();
          categories[data.brand] = data.category;
        });
        setBrandCategories(categories);
      } catch (catError) {
        console.error('Error loading brand categories:', catError);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading items:', error);
      setLoading(false);
    }
  };


  // Helper for add modals - adds item to local state and switches to its category
  const handleItemAdded = (newItem: CollectionItem) => {
    // Skip the next URL sync to prevent flash to 'all'
    skipNextUrlSync.current = true;
    // Add to local state
    setAllItems(prev => [...prev, newItem]);
    // Switch to the item's category so user can see it
    setMainFilter(newItem.itemType);
    // Update URL to match
    const newParams = new URLSearchParams(searchParams);
    newParams.set('type', newItem.itemType);
    setSearchParams(newParams, { replace: true });
    // Reset to page 1
    setCurrentPage(1);
    // Close modal
    setShowAddModal(false);
    setAddItemCategory(null);
  };

  // Handle URL parameters for deep linking from Profile page and Navbar
  useEffect(() => {
    // Skip if we just added an item (prevents flash to 'all')
    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }
    
    // Only process URL params once items are loaded
    if (allItems.length === 0 || loading) return;

    // Get current URL params as string for comparison
    const currentParamsString = searchParams.toString();
    
    // Skip if URL params haven't changed (prevents filter reset on toggle/save)
    if (currentParamsString === lastProcessedParams.current) {
      return;
    }
    
    // Update last processed params
    lastProcessedParams.current = currentParamsString;

    // Check for type param (item category) - ALWAYS sync this
    const typeParam = searchParams.get('type');
    if (typeParam) {
      const validTypes = ['polish', 'plate', 'transfer', 'embellishment', 'tool', 'powder', 'acrylic', 'cuticle_care'];
      if (validTypes.includes(typeParam)) {
        setMainFilter(typeParam);
        // Reset sub-filters when changing main category
        setPolishSubFilter('all');
        setPlateSubFilter('all');
        setTransferSubFilter('all');
        setEmbellishmentSubFilter('all');
      }
    } else if (searchParams.toString() === '' || !searchParams.has('type')) {
      // No type param - only reset to 'all' if we're on a clean /collection URL
      // Don't reset if there are other params like filter, sort, etc.
      if (!searchParams.has('filter') && !searchParams.has('sort') && !searchParams.has('item')) {
        setMainFilter('all');
      }
    }

    // Check for filter param
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      // Map filter param to archiveFilter values
      const archiveFilterMap: Record<string, string> = {
        'favorites': 'favorites',
        'wishlist': 'wishlist',
        'destash': 'for-destash',
        'for-destash': 'for-destash',
        'destashed': 'destashed',
        'rare': 'rare-le',
        'swaps': 'open-to-swaps',
        'needs-info': 'needs-info',
        'swatched': 'swatched',
        'not-swatched': 'not-swatched',
      };
      
      // Map filter param to completenessFilter values
      const completenessFilterMap: Record<string, string> = {
        'no-photo': 'no-photo',
        'no-tags': 'no-tags',
        'no-release': 'no-release',
        'no-description': 'no-description',
        'no-color-family': 'no-color-family',
        'no-storage': 'no-storage',
      };
      
      if (archiveFilterMap[filterParam]) {
        setArchiveFilter(archiveFilterMap[filterParam]);
      } else if (completenessFilterMap[filterParam]) {
        setCompletenessFilter(completenessFilterMap[filterParam]);
      } else {
        // Fallback to direct mapping for archiveFilter
        setArchiveFilter(filterParam);
      }
    }

    // Check for sort param
    const sortParam = searchParams.get('sort');
    if (sortParam) {
      const sortMap: Record<string, string> = {
        'recent': 'most-recent',
        'most-recent': 'most-recent',
        'name': 'name-only',
        'brand': 'brand-name',
        'most-used': 'most-used',
        'least-used': 'least-used'
      };
      const mappedSort = sortMap[sortParam] || sortParam;
      setSortBy(mappedSort);
    }

    // Check for perPage param
    const perPageParam = searchParams.get('perPage');
    if (perPageParam) {
      const perPage = parseInt(perPageParam, 10);
      if ([24, 48, 96].includes(perPage)) {
        setItemsPerPage(perPage);
      }
    }

    // Check for item param to auto-open a specific item
    const itemParam = searchParams.get('item');
    if (itemParam) {
      const itemToOpen = allItems.find(item => item.id === itemParam);
      if (itemToOpen) {
        setSelectedPolish(itemToOpen);
        // Clear just the item param, keep the filter/sort params
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('item');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [allItems, loading, searchParams]);

  // Handle edit item navigation from marketplace
  useEffect(() => {
    // Check if navigated from marketplace with edit intent
    const state = location.state as { editItemId?: string; editItemType?: string } | null;
    
    if (state?.editItemId && allItems.length > 0 && !loading) {
      const itemToEdit = allItems.find(item => item.id === state.editItemId);
      
      if (itemToEdit) {
        // Open the item for editing
        setSelectedPolish(itemToEdit);
        
        // Set the filter to show this item type
        setMainFilter(itemToEdit.itemType);
        
        // Clear the navigation state so it doesn't re-trigger
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [allItems, loading, location.state, navigate, location.pathname]);
  
  /**
   * Custom sort function: numbers/symbols first, then alphabetically
   */
  const sortAlphanumeric = (a: string | undefined | null, b: string | undefined | null): number => {
    const aStr = a ?? '';
    const bStr = b ?? '';
    const aStartsWithLetter = /^[a-zA-Z]/.test(aStr);
    const bStartsWithLetter = /^[a-zA-Z]/.test(bStr);

    if (aStartsWithLetter && !bStartsWithLetter) return 1;
    if (!aStartsWithLetter && bStartsWithLetter) return -1;

    return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
  };

  /**
   * Calculate color similarity between two hex colors (0-100)
   * Uses weighted RGB distance (human eye is more sensitive to green)
   */
  const getColorSimilarity = (hex1: string, hex2: string): number => {
    // Parse hex colors
    const parseHex = (hex: string) => {
      const clean = hex.replace('#', '');
      return {
        r: parseInt(clean.substring(0, 2), 16),
        g: parseInt(clean.substring(2, 4), 16),
        b: parseInt(clean.substring(4, 6), 16)
      };
    };

    const c1 = parseHex(hex1);
    const c2 = parseHex(hex2);

    // Weighted Euclidean distance (human perception weights)
    const rMean = (c1.r + c2.r) / 2;
    const deltaR = c1.r - c2.r;
    const deltaG = c1.g - c2.g;
    const deltaB = c1.b - c2.b;

    // Formula accounts for how humans perceive color differences
    const distance = Math.sqrt(
      (2 + rMean / 256) * deltaR * deltaR +
      4 * deltaG * deltaG +
      (2 + (255 - rMean) / 256) * deltaB * deltaB
    );

    // Max possible distance is ~764, convert to 0-100 similarity
    const maxDistance = 764;
    const similarity = Math.max(0, 100 - (distance / maxDistance) * 100);
    return Math.round(similarity);
  };
  
  /**
   * Sort items based on selected sort option
   */
  const sortItems = (items: CollectionItem[]): CollectionItem[] => {
    const sorted = [...items];
    
    if (sortBy === 'brand-name') {
      sorted.sort((a, b) => {
        const brandCompare = sortAlphanumeric(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;
        return sortAlphanumeric(a.name, b.name);
      });
    } else if (sortBy === 'name-only') {
      sorted.sort((a, b) => sortAlphanumeric(a.name, b.name));
    } else if (sortBy === 'most-recent') {
      sorted.sort((a, b) => {
        const dateA = a.dateAdded instanceof Date ? a.dateAdded : new Date(a.dateAdded);
        const dateB = b.dateAdded instanceof Date ? b.dateAdded : new Date(b.dateAdded);
        return dateB.getTime() - dateA.getTime();
      });
    } else if (sortBy === 'most-used') {
      // Most used items first
      sorted.sort((a, b) => {
        const usedA = a.timesUsed || 0;
        const usedB = b.timesUsed || 0;
        if (usedB !== usedA) return usedB - usedA;
        const brandCompare = sortAlphanumeric(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;
        return sortAlphanumeric(a.name, b.name);
      });
    } else if (sortBy === 'least-used') {
      // Least used (untried) items first
      sorted.sort((a, b) => {
        const usedA = a.timesUsed || 0;
        const usedB = b.timesUsed || 0;
        if (usedA !== usedB) return usedA - usedB;
        const brandCompare = sortAlphanumeric(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;
        return sortAlphanumeric(a.name, b.name);
      });
    } else if (sortBy === 'release-newest') {
      // Newest release year first
      sorted.sort((a, b) => {
        const yearA = a.releaseYear || 0;
        const yearB = b.releaseYear || 0;
        if (yearB !== yearA) return yearB - yearA;
        // Secondary sort by season (Winter=1, Spring=2, Summer=3, Fall=4)
        const seasonOrder: Record<string, number> = { 'Winter': 1, 'Spring': 2, 'Summer': 3, 'Fall': 4 };
        const seasonA = seasonOrder[a.releaseSeason || ''] || 0;
        const seasonB = seasonOrder[b.releaseSeason || ''] || 0;
        if (seasonB !== seasonA) return seasonB - seasonA;
        const brandCompare = sortAlphanumeric(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;
        return sortAlphanumeric(a.name, b.name);
      });
    } else if (sortBy === 'release-oldest') {
      // Oldest release year first
      sorted.sort((a, b) => {
        const yearA = a.releaseYear || 9999;
        const yearB = b.releaseYear || 9999;
        if (yearA !== yearB) return yearA - yearB;
        // Secondary sort by season
        const seasonOrder: Record<string, number> = { 'Winter': 1, 'Spring': 2, 'Summer': 3, 'Fall': 4 };
        const seasonA = seasonOrder[a.releaseSeason || ''] || 5;
        const seasonB = seasonOrder[b.releaseSeason || ''] || 5;
        if (seasonA !== seasonB) return seasonA - seasonB;
        const brandCompare = sortAlphanumeric(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;
        return sortAlphanumeric(a.name, b.name);
      });
    }
    
    return sorted;
  };
  
  // Get active items (exclude deleted and destashed) for building filter dropdown options
  const activeItems = allItems.filter(item => !item.isDeleted && !item.isDestashed);
  
  // Get unique values for filter dropdowns (from active items only)
  const uniqueBrands = [...new Set(activeItems.map(item => item.brand).filter((b): b is string => Boolean(b)))].sort((a, b) => sortAlphanumeric(a, b));
  
  // Get unique collections/events based on selected brand (or all active items if no brand selected)
  const itemsForCollectionFilter = brandFilter === 'all' ? activeItems : activeItems.filter(item => item.brand === brandFilter);
  const uniqueCollectionsEvents = [
    // Get unique collectionNames
    ...itemsForCollectionFilter
      .map(item => item.collectionName)
      .filter((name): name is string => Boolean(name)),
    // Get unique eventThemes
    ...itemsForCollectionFilter
      .map(item => item.eventTheme)
      .filter((theme): theme is string => Boolean(theme)),
  ];
  const uniqueCollectionsEventsList = [...new Set(uniqueCollectionsEvents)].sort((a, b) => sortAlphanumeric(a, b));
  
  const uniqueStorageLocations = [...new Set(activeItems
    .map(item => item.storage)
    .filter(Boolean))] as string[];
  uniqueStorageLocations.sort((a, b) => sortAlphanumeric(a, b));
  
  // Filter items
  const filteredItems = allItems.filter(item => {
    // Main filter with subcategories
    let matchesMain = true;
    if (mainFilter === 'polish') {
      matchesMain = item.itemType === 'polish';
      if (polishSubFilter !== 'all') {
        matchesMain = matchesMain && item.category === polishSubFilter;
      }
    } else if (mainFilter === 'plate') {
      matchesMain = item.itemType === 'plate';
      if (plateSubFilter !== 'all') {
        matchesMain = matchesMain && item.plateType === plateSubFilter;
      }
    } else if (mainFilter === 'transfer') {
      matchesMain = item.itemType === 'transfer';
      if (transferSubFilter !== 'all') {
        // Handle combined types
        if (transferSubFilter === '3D Sticker') {
          matchesMain = matchesMain && ((item.transferType as string) === '3D Stickers' || (item.transferType as string) === '3D Sticker');
        } else if (transferSubFilter === 'Foil') {
          matchesMain = matchesMain && (item.transferType === 'Foil' || (item.transferType as string) === 'Transfer Foil' || (item.transferType as string) === 'Foil Transfer');
        } else if (transferSubFilter === 'Other') {
          matchesMain = matchesMain && (item.transferType as string) === 'Other';
        } else if (transferSubFilter === 'Slider') {
          matchesMain = matchesMain && ((item.transferType as string) === 'Sliders' || (item.transferType as string) === 'Slider');
        } else if (transferSubFilter === 'Sticker') {
          matchesMain = matchesMain && (item.transferType === 'Sticker' || (item.transferType as string) === 'Stickers' || (item.transferType as string) === 'Nail Stickers');
        } else if (transferSubFilter === 'Tattoo') {
          matchesMain = matchesMain && ((item.transferType as string) === 'Tattoos' || (item.transferType as string) === 'Tattoo');
        } else if (transferSubFilter === 'Vinyl') {
          matchesMain = matchesMain && ((item.transferType as string) === 'Vinyls' || (item.transferType as string) === 'Vinyl');
        } else if (transferSubFilter === 'Waterslide Decal') {
          matchesMain = matchesMain && (item.transferType === 'Waterslide Decal' || (item.transferType as string) === 'Water Decal' || (item.transferType as string) === 'Waterslide Decals' || (item.transferType as string) === 'Water Decals');
        } else if (transferSubFilter === 'Wrap') {
          matchesMain = matchesMain && ((item.transferType as string) === 'Wrap' || (item.transferType as string) === 'Wraps' || (item.transferType as string) === 'Nail Wrap');
        } else {
          matchesMain = matchesMain && item.transferType === transferSubFilter;
        }
      }
    } else if (mainFilter === 'embellishment') {
      matchesMain = item.itemType === 'embellishment';
      if (embellishmentSubFilter !== 'all') {
        matchesMain = matchesMain && item.embellishmentType === embellishmentSubFilter;
      }
    } else if (mainFilter === 'tool') {
      matchesMain = item.itemType === 'tool';
    } else if (mainFilter === 'powder') {
      matchesMain = item.itemType === 'powder';
    } else if (mainFilter === 'acrylic') {
      matchesMain = item.itemType === 'acrylic';
    } else if (mainFilter === 'cuticle_care') {
      matchesMain = item.itemType === 'cuticle_care';
    }
    
    const matchesBrand = brandFilter === 'all' || item.brand === brandFilter;
    const matchesStorage = storageFilter === 'all' || item.storage === storageFilter;
    
    // NEW: Collection/Event filter - matches collectionName or eventTheme
    const matchesCollectionEvent = collectionEventFilter === 'all' || 
      item.collectionName === collectionEventFilter || 
      item.eventTheme === collectionEventFilter;
    
    // Archive filter - In Collection / Destashed / Trash / All
    let matchesArchive = true;
    
    // First, filter out deleted items unless we're viewing trash
    if (archiveFilter === 'trash') {
      matchesArchive = item.isDeleted === true;
    } else if (archiveFilter === 'all') {
      // "All" shows everything EXCEPT deleted items
      matchesArchive = item.isDeleted !== true;
    } else if (archiveFilter === 'in-collection') {
      matchesArchive = item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'destashed') {
      matchesArchive = item.isDestashed === true && item.isDeleted !== true;
    } else if (archiveFilter === 'favorites') {
      matchesArchive = item.isFavorite === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'wishlist') {
      matchesArchive = item.onWishlist === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'rare-le') {
      matchesArchive = item.isRareLE === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'needs-info') {
      matchesArchive = item.needsInfo === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'for-destash') {
      matchesArchive = item.isForDestash === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'open-to-swaps') {
      matchesArchive = item.isOpenToSwaps === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'never-used') {
      matchesArchive = (item.timesUsed === 0 || item.timesUsed === undefined) && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'has-notes') {
      matchesArchive = Boolean((item.notes && item.notes.trim() !== '') || (item.publicNotes && item.publicNotes.trim() !== '')) && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'swatched') {
      matchesArchive = item.swatched === true && item.isDestashed !== true && item.isDeleted !== true;
    } else if (archiveFilter === 'not-swatched') {
      matchesArchive = item.swatched !== true && item.isDestashed !== true && item.isDeleted !== true;
    }
    // 'all' shows everything
    
    // NEW: Completeness filter - find items missing data
    let matchesCompleteness = true;
    if (completenessFilter === 'no-photo') {
      const hasPhoto = (item.photos && item.photos.length > 0) || 
                       (item.swatchPhotos && item.swatchPhotos.length > 0) || 
                       (item.bottlePhotos && item.bottlePhotos.length > 0) ||
                       (item.photoUrls && item.photoUrls.length > 0);
      matchesCompleteness = !hasPhoto;
    } else if (completenessFilter === 'no-storage') {
      matchesCompleteness = !item.storage || item.storage.trim() === '';
    } else if (completenessFilter === 'no-tags') {
      const hasTags = (item.finish && item.finish.length > 0) ||
                      (item.particles && item.particles.length > 0) ||
                      (item.specialFeatures && item.specialFeatures.length > 0) ||
                      item.baseType;
      matchesCompleteness = !hasTags;
    } else if (completenessFilter === 'no-color-family') {
      matchesCompleteness = !item.colorFamily || item.colorFamily.length === 0;
    } else if (completenessFilter === 'no-release') {
      const hasRelease = item.releaseYear || item.releaseSeason || (item.releaseType && item.releaseType !== 'unknown');
      matchesCompleteness = !hasRelease;
    } else if (completenessFilter === 'no-description') {
      matchesCompleteness = !item.colorDescription || item.colorDescription.trim() === '';
    }
    
    // NEW: Color family filter
    const matchesColorFamily = colorFamilyFilter === 'all' ||
      (item.colorFamily && item.colorFamily.includes(colorFamilyFilter as any));
    
    // NEW: Release system filters
    let matchesReleaseType = true;
    if (releaseTypeFilter === 'all') {
      matchesReleaseType = true;
    } else if (releaseTypeFilter === 'exclusive') {
      // Match both new structure (releaseType === 'exclusive') and legacy (eventSource starts with "Exclusive")
      matchesReleaseType = item.releaseType === 'exclusive' || item.eventSource?.startsWith('Exclusive') || false;
    } else if (releaseTypeFilter === 'collab') {
      // Collab can be standalone or combined with other types - check for collabWith field
      matchesReleaseType = item.releaseType === 'collab' || !!(item.collabWith && item.collabWith.trim() !== '');
    } else {
      matchesReleaseType = item.releaseType === releaseTypeFilter;
    }
    
    // Exclusive retailer filter - only applies when viewing exclusive release type
    let matchesExclusiveRetailer = true;
    if (releaseTypeFilter === 'exclusive' && exclusiveRetailerFilter !== 'all') {
      if (item.releaseType === 'exclusive' && item.exclusiveTo) {
        matchesExclusiveRetailer = item.exclusiveTo === exclusiveRetailerFilter;
      } else if (item.eventSource?.startsWith('Exclusive')) {
        // Legacy support
        const retailerName = item.eventSource === 'Exclusive' 
          ? 'Exclusive (unspecified)' 
          : item.eventSource.replace('Exclusive to ', '').replace('Exclusive to by ', '');
        matchesExclusiveRetailer = retailerName === exclusiveRetailerFilter;
      } else {
        matchesExclusiveRetailer = false;
      }
    }
    
    // Event source filter - only applies when viewing event release type
    const matchesEventSource = releaseTypeFilter !== 'event' || eventSourceFilter === 'all' || 
      item.eventSource === eventSourceFilter || 
      (item as any).eventName === eventSourceFilter;
    
    // Drill-down filters for release types - only apply when that type is selected
    const matchesBoxName = releaseTypeFilter !== 'box' || boxNameFilter === 'all' || (item as any).boxName === boxNameFilter;
    const matchesGroupName = releaseTypeFilter !== 'groupCustom' || groupNameFilter === 'all' || (item as any).groupName === groupNameFilter;
    const matchesCharityName = releaseTypeFilter !== 'charity' || charityNameFilter === 'all' || (item as any).charityName === charityNameFilter;
    const matchesConventionName = releaseTypeFilter !== 'convention' || conventionNameFilter === 'all' || (item as any).conventionName === conventionNameFilter;
    const matchesCollabWith = releaseTypeFilter !== 'collab' || collabWithFilter === 'all' || item.collabWith === collabWithFilter;
    const matchesCollectionName = releaseTypeFilter !== 'collection' || collectionNameFilter === 'all' || item.collectionName === collectionNameFilter;
    
    const matchesYear = yearFilter === 'all' || item.releaseYear?.toString() === yearFilter;
    const matchesSeason = seasonFilter === 'all' || item.releaseSeason === seasonFilter;
    
    let matchesQuantity = true;
    if (quantityFilter === 'none') {
      matchesQuantity = item.quantity === 0;
    } else if (quantityFilter === 'has' || quantityFilter === 'multiples') {
      // 'has' kept for backwards compatibility, 'multiples' is the new name
      matchesQuantity = (item.quantity || 1) >= 2;
    } else if (quantityFilter === 'single') {
      matchesQuantity = (item.quantity || 1) === 1;
    }
    
    const matchesSearch = searchQuery === '' || 
      (item.brand && item.brand.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.colorDescription && item.colorDescription.toLowerCase().includes(searchQuery.toLowerCase())) ||
      // Search through product line (e.g., "Insta-Dri", "Ultra Chrome")
      ((item as any).line && (item as any).line.toLowerCase().includes(searchQuery.toLowerCase())) ||
      // Search through swatch number
      ((item as any).swatchNumber && (item as any).swatchNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
      // Search through plate image tags
      (item.imageTags && item.imageTags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))) ||
      // Search through generic tags
      (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))) ||
      // Search through collection name
      (item.collectionName && item.collectionName.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Tag filter - now works with structured tags
    // tagFilter format: "category:value" e.g. "finish:shimmer" or "particles:glitter"
    let matchesTag = true;
    if (tagFilter !== '') {
      const [category, value] = tagFilter.split(':');
      switch (category) {
        case 'baseType':
          matchesTag = item.baseType === value;
          break;
        case 'finish':
          matchesTag = item.finish?.includes(value as any) || false;
          break;
        case 'holoType':
          matchesTag = item.holoType?.includes(value as any) || false;
          break;
        case 'colorShift':
          matchesTag = item.colorShift === value;
          break;
        case 'particles':
          matchesTag = item.particles?.includes(value as any) || false;
          break;
        case 'specialFeatures':
          matchesTag = item.specialFeatures?.includes(value as any) || false;
          break;
        case 'colorFamily':
          matchesTag = item.colorFamily?.includes(value as any) || false;
          break;
        case 'utility':
          matchesTag = item.utility?.includes(value as any) || false;
          break;
        case 'colorCharacter':
          matchesTag = item.colorCharacter?.includes(value as any) || false;
          break;
        case 'collectionName':
          // Filter by collection name (from card click)
          matchesTag = item.collectionName === value;
          break;
        case 'imageTags':
          // Filter by plate image tags
          matchesTag = item.imageTags?.includes(value) || false;
          break;
        case 'tags':
          // Filter by generic tags array
          matchesTag = item.tags?.includes(value) || false;
          break;
        default:
          matchesTag = true;
      }
    }

    // Color search filter - check if any of item's colors are similar enough
    let matchesColorSearch = true;
    if (searchColor) {
      // Get all colors for this item (new colors array or legacy primaryColor)
      const itemColors = (item.colorFamily && item.colorFamily.length > 0)
        ? item.colorFamily
        : item.primaryColor
          ? [item.primaryColor]
          : [];

      if (itemColors && itemColors.length > 0) {
        // Check if ANY of the item's colors match the search
        matchesColorSearch = itemColors.some((color: string) => {
          const similarity = getColorSimilarity(searchColor, color);
          return similarity >= colorSimilarity;
        });
      } else {
        // If searching by color but item has no colors, don't include it
        matchesColorSearch = false;
      }
    }
    
    // Hide wishlist items toggle (when enabled, exclude items that are on wishlist)
    // Only applies when not specifically viewing wishlist items
    const matchesWishlistToggle = !hideWishlistItems || archiveFilter === 'wishlist' || item.onWishlist !== true;
    
    return matchesMain && matchesBrand && matchesCollectionEvent && matchesStorage && matchesArchive && matchesQuantity && matchesSearch && matchesTag && matchesReleaseType && matchesEventSource && matchesExclusiveRetailer && matchesBoxName && matchesGroupName && matchesCharityName && matchesConventionName && matchesCollabWith && matchesCollectionName && matchesYear && matchesSeason && matchesCompleteness && matchesColorFamily && matchesColorSearch && matchesWishlistToggle;
  });
  
  // Sort filtered items
  const sortedItems = sortItems(filteredItems);

  // Navigation helpers for detail modals
  const getSelectedItemIndex = () => {
    if (!selectedPolish) return -1;
    return sortedItems.findIndex(item => item.id === selectedPolish.id);
  };

  const handleNavigatePrev = () => {
    const currentIndex = getSelectedItemIndex();
    if (currentIndex > 0) {
      setSelectedPolish(sortedItems[currentIndex - 1]);
    }
  };

  const handleNavigateNext = () => {
    const currentIndex = getSelectedItemIndex();
    if (currentIndex < sortedItems.length - 1) {
      setSelectedPolish(sortedItems[currentIndex + 1]);
    }
  };

  const hasPrevItem = () => getSelectedItemIndex() > 0;
  const hasNextItem = () => {
    const idx = getSelectedItemIndex();
    return idx >= 0 && idx < sortedItems.length - 1;
  };
  
  // Pagination
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = sortedItems.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, mainFilter, polishSubFilter, plateSubFilter, transferSubFilter, embellishmentSubFilter, sortBy, brandFilter, collectionEventFilter, storageFilter, quantityFilter, tagFilter, archiveFilter, releaseTypeFilter, eventSourceFilter, exclusiveRetailerFilter, boxNameFilter, groupNameFilter, charityNameFilter, conventionNameFilter, collabWithFilter, collectionNameFilter, yearFilter, seasonFilter, colorFamilyFilter, completenessFilter, searchColor, colorSimilarity, hideWishlistItems]);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);
  
  // Show toast notification with optional undo action
  const showToast = (
    message: string, 
    type: 'success' | 'info' | 'warning' = 'success',
    undoAction?: () => void
  ) => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    setToast({ message, type, undoAction });
    
    // Auto-dismiss after 4 seconds
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 4000);
  };
  
  // Dismiss toast
  const dismissToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast(null);
  };
  
  // Handle item save from modal
  const handleSaveItem = async (updatedItem: CollectionItem) => {
    // Get current user
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('No user logged in!');
      return;
    }

    // Find the original item to detect what changed (for toast notifications)
    const originalItem = allItems.find(i => i.id === updatedItem.id);

    try {
      // ✅ FIXED: Use nested user path instead of root items path
      const itemRef = doc(db, 'users', currentUser.uid, 'items', updatedItem.id);

      // Build update object - only include fields that are NOT undefined
      const updateData: any = {};

      if (updatedItem.name !== undefined) updateData.name = updatedItem.name;
      if (updatedItem.brand !== undefined) updateData.brand = updatedItem.brand;
      if (updatedItem.category !== undefined) updateData.category = updatedItem.category;
      if (updatedItem.transferType !== undefined) updateData.transferType = updatedItem.transferType;
      if (updatedItem.embellishmentType !== undefined) updateData.embellishmentType = updatedItem.embellishmentType;
      if (updatedItem.plateType !== undefined) updateData.plateType = updatedItem.plateType;
      if (updatedItem.plateSize !== undefined) updateData.plateSize = updatedItem.plateSize;
      if (updatedItem.numberOfImages !== undefined) updateData.numberOfImages = updatedItem.numberOfImages;
      // Type-specific fields that were previously silently dropped on edit:
      // tools (toolType/material), powders (powderType/effect), acrylics
      // (acrylicType/system), cuticle care (cuticleCareType). `size` and
      // `color` are shared across tool/powder/acrylic/embellishment/cuticle.
      if (updatedItem.toolType !== undefined) updateData.toolType = updatedItem.toolType;
      if (updatedItem.material !== undefined) updateData.material = updatedItem.material;
      if (updatedItem.size !== undefined) updateData.size = updatedItem.size;
      if (updatedItem.color !== undefined) updateData.color = updatedItem.color;
      if (updatedItem.powderType !== undefined) updateData.powderType = updatedItem.powderType;
      if (updatedItem.effect !== undefined) updateData.effect = updatedItem.effect;
      if (updatedItem.acrylicType !== undefined) updateData.acrylicType = updatedItem.acrylicType;
      if (updatedItem.system !== undefined) updateData.system = updatedItem.system;
      if (updatedItem.cuticleCareType !== undefined) updateData.cuticleCareType = updatedItem.cuticleCareType;
      if (updatedItem.collection !== undefined) updateData.collection = updatedItem.collection;
      if (updatedItem.event !== undefined) updateData.event = updatedItem.event;
      if (updatedItem.quantity !== undefined) updateData.quantity = updatedItem.quantity;
      if (updatedItem.storage !== undefined) updateData.storage = updatedItem.storage;
      if (updatedItem.barcode !== undefined) updateData.barcode = updatedItem.barcode;
      if (updatedItem.price !== undefined) updateData.price = updatedItem.price;
      if (updatedItem.priceCurrency !== undefined) updateData.priceCurrency = updatedItem.priceCurrency;
      if (updatedItem.purchaseDate !== undefined) updateData.purchaseDate = updatedItem.purchaseDate;
      if (updatedItem.purchaseSource !== undefined) updateData.purchaseSource = updatedItem.purchaseSource;
      if (updatedItem.notes !== undefined) updateData.notes = updatedItem.notes;
      if (updatedItem.publicNotes !== undefined) updateData.publicNotes = updatedItem.publicNotes;
      if (updatedItem.colorDescription !== undefined) updateData.colorDescription = updatedItem.colorDescription;
      if (updatedItem.onWishlist !== undefined) updateData.onWishlist = updatedItem.onWishlist;
      if (updatedItem.isoPriority !== undefined) updateData.isoPriority = updatedItem.isoPriority;
      if (updatedItem.needsInfo !== undefined) updateData.needsInfo = updatedItem.needsInfo;
      if (updatedItem.isForDestash !== undefined) updateData.isForDestash = updatedItem.isForDestash;
      if (updatedItem.isPrivate !== undefined) updateData.isPrivate = updatedItem.isPrivate;
      
      // ✅ FIXED: When removing from destash (isForDestash === false), delete the related fields
      // Otherwise, only update if they have values
      if (updatedItem.isForDestash === false) {
        // Explicitly delete destash fields when unlisting
        updateData.destashPrice = deleteField();
        updateData.destashCondition = deleteField();
        updateData.destashNotes = deleteField();
        updateData.isOpenToSwaps = deleteField();
        updateData.destashStatus = deleteField();
      } else {
        // Normal update - only set if defined
        if (updatedItem.destashPrice !== undefined) updateData.destashPrice = updatedItem.destashPrice;
        if (updatedItem.destashCondition !== undefined) updateData.destashCondition = updatedItem.destashCondition;
        if (updatedItem.destashNotes !== undefined) updateData.destashNotes = updatedItem.destashNotes;
        if (updatedItem.isOpenToSwaps !== undefined) updateData.isOpenToSwaps = updatedItem.isOpenToSwaps;
        if (updatedItem.destashStatus !== undefined) updateData.destashStatus = updatedItem.destashStatus;
      }

      // ✅ NEW: Add usage tracking fields
      if (updatedItem.isFavorite !== undefined) updateData.isFavorite = updatedItem.isFavorite;
      if (updatedItem.isRareLE !== undefined) updateData.isRareLE = updatedItem.isRareLE;
      if (updatedItem.swatched !== undefined) updateData.swatched = updatedItem.swatched;
      if (updatedItem.timesUsed !== undefined) updateData.timesUsed = updatedItem.timesUsed;
      if (updatedItem.lastUsedDate !== undefined) updateData.lastUsedDate = updatedItem.lastUsedDate;

      // ✅ NEW: Add photos field
      if (updatedItem.photos !== undefined) updateData.photos = updatedItem.photos;

      // ✅ NEW: Add structured tags
      if (updatedItem.baseType !== undefined) updateData.baseType = updatedItem.baseType;
      if (updatedItem.finish !== undefined) updateData.finish = updatedItem.finish;
      if (updatedItem.holoType !== undefined) updateData.holoType = updatedItem.holoType;
      if (updatedItem.colorShift !== undefined) updateData.colorShift = updatedItem.colorShift;
      if (updatedItem.particles !== undefined) updateData.particles = updatedItem.particles;
      if (updatedItem.specialFeatures !== undefined) updateData.specialFeatures = updatedItem.specialFeatures;
      if (updatedItem.colorFamily !== undefined) updateData.colorFamily = updatedItem.colorFamily;
      if (updatedItem.utility !== undefined) updateData.utility = updatedItem.utility;
      if (updatedItem.colorCharacter !== undefined) updateData.colorCharacter = updatedItem.colorCharacter;

      // ✅ NEW: Add release system fields
      if (updatedItem.releaseType !== undefined) updateData.releaseType = updatedItem.releaseType;
      if (updatedItem.collectionName !== undefined) updateData.collectionName = updatedItem.collectionName;
      if (updatedItem.eventSource !== undefined) updateData.eventSource = updatedItem.eventSource;
      if (updatedItem.eventTheme !== undefined) updateData.eventTheme = updatedItem.eventTheme;
      if (updatedItem.releaseYear !== undefined) updateData.releaseYear = updatedItem.releaseYear;
      if (updatedItem.releaseSeason !== undefined) updateData.releaseSeason = updatedItem.releaseSeason;
      if (updatedItem.releaseMonth !== undefined) updateData.releaseMonth = updatedItem.releaseMonth;
      if (updatedItem.releaseNotes !== undefined) updateData.releaseNotes = updatedItem.releaseNotes;

      // ✅ NEW: Add additional release info fields
      if (updatedItem.eventName !== undefined) updateData.eventName = updatedItem.eventName;
      if (updatedItem.boxName !== undefined) updateData.boxName = updatedItem.boxName;
      if (updatedItem.boxTheme !== undefined) updateData.boxTheme = updatedItem.boxTheme;
      if (updatedItem.box !== undefined) updateData.box = updatedItem.box;
      if (updatedItem.groupName !== undefined) updateData.groupName = updatedItem.groupName;
      if (updatedItem.groupTheme !== undefined) updateData.groupTheme = updatedItem.groupTheme;
      if (updatedItem.exclusiveTo !== undefined) updateData.exclusiveTo = updatedItem.exclusiveTo;
      if (updatedItem.exclusiveTheme !== undefined) updateData.exclusiveTheme = updatedItem.exclusiveTheme;
      if (updatedItem.charityName !== undefined) updateData.charityName = updatedItem.charityName;
      if (updatedItem.charityCause !== undefined) updateData.charityCause = updatedItem.charityCause;
      if (updatedItem.conventionName !== undefined) updateData.conventionName = updatedItem.conventionName;
      if (updatedItem.collabWith !== undefined) updateData.collabWith = updatedItem.collabWith;
      if (updatedItem.collabTheme !== undefined) updateData.collabTheme = updatedItem.collabTheme;
      if (updatedItem.isRerelease !== undefined) updateData.isRerelease = updatedItem.isRerelease;
      if (updatedItem.originalReleaseYear !== undefined) updateData.originalReleaseYear = updatedItem.originalReleaseYear;
      if (updatedItem.originalReleaseSeason !== undefined) updateData.originalReleaseSeason = updatedItem.originalReleaseSeason;
      if (updatedItem.setType !== undefined) updateData.setType = updatedItem.setType;
      if (updatedItem.setName !== undefined) updateData.setName = updatedItem.setName;
      if (updatedItem.line !== undefined) updateData.line = updatedItem.line;
      if (updatedItem.swatchNumber !== undefined) updateData.swatchNumber = updatedItem.swatchNumber;

      // ✅ NEW: Add destash system fields (for archive tracking, separate from marketplace listing)
      if (updatedItem.isDestashed !== undefined) updateData.isDestashed = updatedItem.isDestashed;
      if (updatedItem.destashDate !== undefined) updateData.destashDate = updatedItem.destashDate;
      if (updatedItem.destashMethod !== undefined) updateData.destashMethod = updatedItem.destashMethod;
      if (updatedItem.destashSalePrice !== undefined) updateData.destashSalePrice = updatedItem.destashSalePrice;

      // ✅ NEW: Add generic tags field
      if (updatedItem.tags !== undefined) updateData.tags = updatedItem.tags;

      // Save to Firebase
      await updateDoc(itemRef, updateData);

      // Reload JUST this one item from Firebase (with fresh data, no cache)
      const freshDocSnap = await getDoc(itemRef);
      const freshData = freshDocSnap.data();

      if (freshData) {
        const freshItem: CollectionItem = {
          id: freshDocSnap.id,
          ...freshData,
          dateAdded: freshData.dateAdded?.toDate?.() || freshData.createdAt?.toDate?.() || new Date(),
        } as CollectionItem;

        // Update this one item in allItems
        setAllItems(prevItems =>
          prevItems.map(item => item.id === freshItem.id ? freshItem : item)
        );

        // Update selectedPolish if it's the same item
        if (selectedPolish?.id === freshItem.id) {
          setSelectedPolish(freshItem);
        }
        
        // Show toast for toggle actions (compare with original to detect changes)
        if (originalItem) {
          // Create undo function
          const createUndoAction = (field: keyof CollectionItem, previousValue: any) => {
            return async () => {
              const revertItem = { ...freshItem, [field]: previousValue };
              await handleSaveItem(revertItem);
              dismissToast();
            };
          };
          
          // Check for toggle changes and show appropriate toast
          if (originalItem.isFavorite !== updatedItem.isFavorite) {
            if (updatedItem.isFavorite) {
              showToast(`⭐ Added to favorites`, 'success', createUndoAction('isFavorite', false));
            } else {
              showToast(`Removed from favorites`, 'info', createUndoAction('isFavorite', true));
            }
          } else if (originalItem.onWishlist !== updatedItem.onWishlist) {
            if (updatedItem.onWishlist) {
              showToast(`⭐ Added to wishlist`, 'success', createUndoAction('onWishlist', false));
            } else {
              showToast(`Removed from wishlist`, 'info', createUndoAction('onWishlist', true));
            }
          } else if (originalItem.isForDestash !== updatedItem.isForDestash) {
            if (updatedItem.isForDestash) {
              showToast(`🛍️ Listed for destash`, 'success', createUndoAction('isForDestash', false));
            } else {
              showToast(`Removed from destash`, 'info', createUndoAction('isForDestash', true));
            }
          } else if (originalItem.isOpenToSwaps !== updatedItem.isOpenToSwaps) {
            if (updatedItem.isOpenToSwaps) {
              showToast(`🔄 Open to swaps`, 'success', createUndoAction('isOpenToSwaps', false));
            } else {
              showToast(`No longer open to swaps`, 'info', createUndoAction('isOpenToSwaps', true));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  // Delete item permanently
  // SOFT DELETE - moves item to trash instead of permanently deleting
  const handleDeleteItem = async (itemId: string) => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('No user logged in!');
      return;
    }

    try {
      const { updateDoc, Timestamp } = await import('firebase/firestore');
      const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
      
      // Soft delete: mark as deleted with timestamp
      await updateDoc(itemRef, {
        isDeleted: true,
        deletedAt: Timestamp.now()
      });
      
      // Update local state
      setAllItems(prevItems => prevItems.map(item => 
        item.id === itemId 
          ? { ...item, isDeleted: true, deletedAt: new Date() }
          : item
      ));
      
      // Close modal if this item was selected
      if (selectedPolish?.id === itemId) {
        setSelectedPolish(null);
      }
    } catch (error) {
      console.error('Error moving item to trash:', error);
    }
  };

  // RESTORE from trash
  const handleRestoreItem = async (itemId: string) => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('No user logged in!');
      return;
    }

    try {
      const { updateDoc, deleteField } = await import('firebase/firestore');
      const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
      
      // Remove delete markers
      await updateDoc(itemRef, {
        isDeleted: deleteField(),
        deletedAt: deleteField()
      });
      
      // Update local state
      setAllItems(prevItems => prevItems.map(item => 
        item.id === itemId 
          ? { ...item, isDeleted: undefined, deletedAt: undefined }
          : item
      ));
      
      // Close modal
      if (selectedPolish?.id === itemId) {
        setSelectedPolish(null);
      }
    } catch (error) {
      console.error('Error restoring item:', error);
    }
  };

  // PERMANENT DELETE - actually removes from database
  const handlePermanentDeleteItem = async (itemId: string) => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('No user logged in!');
      return;
    }

    try {
      const { deleteDoc } = await import('firebase/firestore');
      const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
      await deleteDoc(itemRef);
      
      // Remove from local state
      setAllItems(prevItems => prevItems.filter(item => item.id !== itemId));
      
      // Close modal if this item was selected
      if (selectedPolish?.id === itemId) {
        setSelectedPolish(null);
      }
    } catch (error) {
      console.error('Error permanently deleting item:', error);
    }
  };

  // SELECTION MODE HANDLERS
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems(new Set()); // Clear selections when toggling mode
  };

  const handleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Select/deselect all items on current page
  const handleSelectAllPage = () => {
    if (selectedItems.size === paginatedItems.length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all visible items on current page
      const allIds = new Set(paginatedItems.map(item => item.id));
      setSelectedItems(allIds);
    }
  };

  // Select ALL filtered items (for export, etc.)
  const handleSelectAllFiltered = () => {
    if (selectedItems.size === sortedItems.length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select ALL filtered items
      const allIds = new Set(sortedItems.map(item => item.id));
      setSelectedItems(allIds);
    }
  };

  // BULK ACTION HANDLERS
  const handleBulkToggle = async (field: 'onWishlist' | 'needsInfo' | 'isForDestash' | 'isOpenToSwaps' | 'isFavorite' | 'isRareLE' | 'swatched', value: boolean) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      const itemIds = Array.from(selectedItems);
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        // When adding to wishlist, also set default ISO priority
        if (field === 'onWishlist' && value) {
          await updateDoc(itemRef, { [field]: value, isoPriority: 'iso' });
        } else {
          await updateDoc(itemRef, { [field]: value });
        }
      });

      await Promise.all(updates);
      
      // Update local state instead of reloading
      setAllItems(prev => prev.map(item => {
        if (itemIds.includes(item.id)) {
          // When adding to wishlist, also set default ISO priority in local state
          if (field === 'onWishlist' && value) {
            return { ...item, [field]: value, isoPriority: 'iso' as const };
          }
          return { ...item, [field]: value };
        }
        return item;
      }));
      
      setSelectedItems(new Set()); // Clear selection but keep selection mode open
    } catch (error) {
      console.error('Error updating items:', error);
      alert('Failed to update items. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedItems.size} item(s)? This cannot be undone.`
    );
    if (!confirmed) return;

    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      const { deleteDoc } = await import('firebase/firestore');
      const itemIds = Array.from(selectedItems);
      const deletions = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await deleteDoc(itemRef);
      });

      await Promise.all(deletions);
      
      // Update local state instead of reloading
      setAllItems(prev => prev.filter(item => !itemIds.includes(item.id)));
      
      setSelectedItems(new Set());
      setIsSelectionMode(false);
      alert(`Successfully deleted ${deletions.length} item(s).`);
    } catch (error) {
      console.error('Error deleting items:', error);
      alert('Failed to delete items. Please try again.');
    }
  };

  const handleBulkDestash = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      const itemIds = Array.from(selectedItems);
      const destashDate = new Date();
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await updateDoc(itemRef, {
          isDestashed: true,
          destashDate: destashDate,
          isForDestash: false
        });
      });

      await Promise.all(updates);

      // Update local state instead of reloading
      setAllItems(prev => prev.map(item =>
        itemIds.includes(item.id)
          ? { ...item, isDestashed: true, destashDate: destashDate, isForDestash: false }
          : item
      ));

      setSelectedItems(new Set());
      setIsSelectionMode(false);
      alert(`Moved ${updates.length} item(s) to destash archive.`);
    } catch (error) {
      console.error('Error destashing items:', error);
      alert('Failed to destash items. Please try again.');
    }
  };

  const handleBulkStorage = async () => {
    const storage = prompt('Enter storage location for selected items:');
    if (!storage) return;

    const currentUser = getCurrentUser();
    if (!currentUser) return;

    const itemIds = Array.from(selectedItems);
    const selectedItemsData = allItems.filter(item => itemIds.includes(item.id));
    const trimmedStorage = storage.trim();
    
    // Show confirmation with details
    const itemNames = selectedItemsData.slice(0, 5).map(item => `• ${item.brand} ${item.name}`).join('\n');
    const moreCount = selectedItemsData.length > 5 ? `\n...and ${selectedItemsData.length - 5} more` : '';
    
    const confirmed = window.confirm(
      `Set storage to "${trimmedStorage}" for ${selectedItemsData.length} item(s)?\n\n${itemNames}${moreCount}`
    );
    
    if (!confirmed) return;

    try {
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await updateDoc(itemRef, { storage: trimmedStorage });
      });

      await Promise.all(updates);
      
      // Update local state instead of reloading
      setAllItems(prev => prev.map(item => 
        itemIds.includes(item.id) ? { ...item, storage: trimmedStorage } : item
      ));
      
      setSelectedItems(new Set()); // Clear selection but keep selection mode open
    } catch (error) {
      console.error('Error updating storage:', error);
      alert('Failed to update storage. Please try again.');
    }
  };

  const handleBulkPrice = async () => {
    const priceInput = prompt('Enter purchase price for selected items (e.g., 12.50):');
    if (!priceInput) return;
    
    const price = parseFloat(priceInput.replace(/[$,]/g, ''));
    if (isNaN(price) || price < 0) {
      alert('Please enter a valid price (e.g., 12.50)');
      return;
    }

    const currentUser = getCurrentUser();
    if (!currentUser) return;

    // Get selected items for confirmation
    const itemIds = Array.from(selectedItems);
    const selectedItemsData = allItems.filter(item => itemIds.includes(item.id));
    
    // Show confirmation with details
    const itemNames = selectedItemsData.slice(0, 5).map(item => `• ${item.brand} ${item.name}`).join('\n');
    const moreCount = selectedItemsData.length > 5 ? `\n...and ${selectedItemsData.length - 5} more` : '';
    
    const confirmed = window.confirm(
      `Set price to $${price.toFixed(2)} for ${selectedItemsData.length} item(s)?\n\n${itemNames}${moreCount}`
    );
    
    if (!confirmed) return;

    try {
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await updateDoc(itemRef, { price });
      });

      await Promise.all(updates);
      
      // Update local state instead of reloading
      setAllItems(prev => prev.map(item => 
        itemIds.includes(item.id) ? { ...item, price } : item
      ));
      
      setSelectedItems(new Set()); // Clear selection but keep selection mode open
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Failed to update price. Please try again.');
    }
  };

  const handleBulkLine = async () => {
    const line = prompt('Enter line/collection name for selected items (e.g., "Insta-Dri", "Ultra Chrome"):');
    if (!line) return;

    const currentUser = getCurrentUser();
    if (!currentUser) return;

    const itemIds = Array.from(selectedItems);
    const selectedItemsData = allItems.filter(item => itemIds.includes(item.id));
    const trimmedLine = line.trim();

    // Show confirmation with details
    const itemNames = selectedItemsData.slice(0, 5).map(item => `• ${item.brand} ${item.name}`).join('\n');
    const moreCount = selectedItemsData.length > 5 ? `\n...and ${selectedItemsData.length - 5} more` : '';

    const confirmed = window.confirm(
      `Set line to "${trimmedLine}" for ${selectedItemsData.length} item(s)?\n\n${itemNames}${moreCount}`
    );

    if (!confirmed) return;

    try {
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await updateDoc(itemRef, { line: trimmedLine });
      });

      await Promise.all(updates);

      // Update local state instead of reloading
      setAllItems(prev => prev.map(item =>
        itemIds.includes(item.id) ? { ...item, line: trimmedLine } : item
      ));

      setSelectedItems(new Set()); // Clear selection but keep selection mode open
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Failed to update line. Please try again.');
    }
  };

  const handleBulkCategory = async () => {
    const category = prompt('Enter category for selected items (e.g., "Cremes", "Glitters", "Holos"):');
    if (!category) return;

    const currentUser = getCurrentUser();
    if (!currentUser) return;

    const itemIds = Array.from(selectedItems);
    const selectedItemsData = allItems.filter(item => itemIds.includes(item.id));
    const trimmedCategory = category.trim();

    // Show confirmation with details
    const itemNames = selectedItemsData.slice(0, 5).map(item => `• ${item.brand} ${item.name}`).join('\n');
    const moreCount = selectedItemsData.length > 5 ? `\n...and ${selectedItemsData.length - 5} more` : '';

    const confirmed = window.confirm(
      `Set category to "${trimmedCategory}" for ${selectedItemsData.length} item(s)?\n\n${itemNames}${moreCount}`
    );

    if (!confirmed) return;

    try {
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await updateDoc(itemRef, { category: trimmedCategory });
      });

      await Promise.all(updates);

      // Update local state instead of reloading
      setAllItems(prev => prev.map(item =>
        itemIds.includes(item.id) ? { ...item, category: trimmedCategory as CollectionItem['category'] } : item
      ));

      setSelectedItems(new Set()); // Clear selection but keep selection mode open
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category. Please try again.');
    }
  };

  const handleBulkExportCSV = () => {
    const selectedItemsData = allItems.filter(item => selectedItems.has(item.id));
    
    // CSV headers - comprehensive export matching import template fields
    const headers = [
      'Brand', 'Name', 'Category', 'Color Description', 'Line', 'Swatch Number',
      'Finish', 'Color Family', 'Base Type', 'Holo Type', 'Color Shift',
      'Particles', 'Special Features', 'Utility', 'Color Character',
      'Collection Name', 'Release Type', 'Release Year', 'Release Season',
      'Event Source', 'Event Theme',
      'Box Name', 'Box Theme', 'Group Name', 'Group Theme',
      'Exclusive To', 'Exclusive Theme', 'Charity Name', 'Charity Cause',
      'Convention Name', 'Collab With', 'Collab Theme',
      'Set Name', 'Set Type',
      'Type', 'Quantity', 'Storage', 'Price', 'Barcode', 'Tags', 'Notes',
      'Swatched', 'Times Used', 'Last Used Date',
      'Favorite', 'On Wishlist', 'Rare/LE', 'ISO Priority', 'Needs Info',
      'For Destash', 'Destash Price', 'Destash Condition', 'Open to Swaps'
    ];
    
    // Helper: join arrays into comma-separated strings for CSV
    const joinArr = (arr: any) => Array.isArray(arr) ? arr.join(', ') : (arr || '');
    
    // CSV rows
    const rows = selectedItemsData.map(item => [
      item.brand || '',
      item.name || '',
      item.category || (item as any).polishType || '',
      item.colorDescription || '',
      (item as any).line || '',
      (item as any).swatchNumber || '',
      joinArr(item.finish),
      joinArr(item.colorFamily),
      item.baseType || '',
      joinArr(item.holoType),
      item.colorShift || '',
      joinArr(item.particles),
      joinArr(item.specialFeatures),
      joinArr(item.utility),
      joinArr(item.colorCharacter),
      item.collectionName || '',
      item.releaseType || '',
      item.releaseYear || '',
      item.releaseSeason || '',
      item.eventSource || '',
      item.eventTheme || '',
      (item as any).boxName || '',
      (item as any).boxTheme || '',
      (item as any).groupName || '',
      (item as any).groupTheme || '',
      (item as any).exclusiveTo || '',
      (item as any).exclusiveTheme || '',
      (item as any).charityName || '',
      (item as any).charityCause || '',
      (item as any).conventionName || '',
      (item as any).collabWith || '',
      (item as any).collabTheme || '',
      (item as any).setName || '',
      (item as any).setType || '',
      item.itemType || '',
      item.quantity || 1,
      item.storage || '',
      item.price || '',
      item.barcode || '',
      joinArr(item.tags),
      item.notes || '',
      item.swatched ? 'Yes' : 'No',
      item.timesUsed || 0,
      item.lastUsedDate || '',
      item.isFavorite ? 'Yes' : 'No',
      item.onWishlist ? 'Yes' : 'No',
      item.isRareLE ? 'Yes' : 'No',
      item.isoPriority || '',
      item.needsInfo ? 'Yes' : 'No',
      item.isForDestash ? 'Yes' : 'No',
      item.destashPrice || '',
      item.destashCondition || '',
      item.isOpenToSwaps ? 'Yes' : 'No'
    ]);

    // Build CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `polish-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setSelectedItems(new Set()); // Clear selection but keep selection mode open
  };

  // BULK CHANGE ITEM TYPE
  const handleBulkChangeType = async (newType: string) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      const itemIds = Array.from(selectedItems);
      const updates = itemIds.map(async (itemId) => {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        await updateDoc(itemRef, { itemType: newType });
      });

      await Promise.all(updates);
      
      // Update local state instead of reloading
      setAllItems(prev => prev.map(item =>
        itemIds.includes(item.id) ? { ...item, itemType: newType as CollectionItem['itemType'] } : item
      ));
      
      setSelectedItems(new Set()); // Clear selection but keep selection mode open
      setShowChangeTypeModal(false);
      alert(`Successfully changed ${itemIds.length} item(s) to ${newType}!`);
    } catch (error) {
      console.error('Error changing item types:', error);
      alert('Error changing item types. Please try again.');
    }
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setMainFilter('all');
    setPolishSubFilter('all');
    setPlateSubFilter('all');
    setTransferSubFilter('all');
    setEmbellishmentSubFilter('all');
    setSortBy('brand-name');
    setBrandFilter('all');
    setCollectionEventFilter('all');
    setStorageFilter('all');
    setQuantityFilter('any');
    setTagFilter('');
    setArchiveFilter('in-collection');
    // Release filters
    setReleaseTypeFilter('all');
    setEventSourceFilter('all');
    setExclusiveRetailerFilter('all');
    setBoxNameFilter('all');
    setGroupNameFilter('all');
    setCharityNameFilter('all');
    setConventionNameFilter('all');
    setCollabWithFilter('all');
    setCollectionNameFilter('all');
    setYearFilter('all');
    setSeasonFilter('all');
    // New filters
    setColorFamilyFilter('all');
    setCompletenessFilter('all');
    // Color search
    setSearchColor(null);
    setColorSimilarity(80);
  };

  // Helper to set tag filter and reset to page 1
  const handleTagFilterClick = (category: string, value: string) => {
    setTagFilter(`${category}:${value}`);
    setCurrentPage(1);
  };

  // Helper to filter by collection name from card click
  const handleCollectionFilterClick = (collectionName: string) => {
    // Use the tag filter with a special prefix for collection
    setTagFilter(`collectionName:${collectionName}`);
    setCurrentPage(1);
  };

  // Helper to filter by event source from card click
  const handleEventFilterClick = (eventSource: string) => {
    setEventSourceFilter(eventSource);
    setCurrentPage(1);
  };

  // ONE-TIME MIGRATION: Set all plates to "Stamping Plate" type
  // DELETE THIS FUNCTION AFTER RUNNING ONCE
  // Toggle sidebar filter sections
  const toggleFilterSection = (section: string) => {
    const newExpanded = new Set(expandedFilterSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedFilterSections(newExpanded);
  };
  
  // Check if any filters are active
  const hasActiveFilters = searchQuery !== '' || 
    mainFilter !== 'all' || 
    polishSubFilter !== 'all' ||
    transferSubFilter !== 'all' ||
    embellishmentSubFilter !== 'all' ||
    brandFilter !== 'all' || 
    collectionEventFilter !== 'all' ||
    storageFilter !== 'all' || 
    quantityFilter !== 'any' || 
    tagFilter !== '' ||
    archiveFilter !== 'in-collection' ||
    releaseTypeFilter !== 'all' ||
    eventSourceFilter !== 'all' ||
    exclusiveRetailerFilter !== 'all' ||
    boxNameFilter !== 'all' ||
    groupNameFilter !== 'all' ||
    charityNameFilter !== 'all' ||
    conventionNameFilter !== 'all' ||
    collabWithFilter !== 'all' ||
    collectionNameFilter !== 'all' ||
    yearFilter !== 'all' ||
    seasonFilter !== 'all' ||
    colorFamilyFilter !== 'all' ||
    completenessFilter !== 'all' ||
    searchColor !== null;
  
  // Check if "more filters" section has active filters (to show indicator)
  const hasMoreFiltersActive = 
    storageFilter !== 'all' || 
    quantityFilter !== 'any' || 
    releaseTypeFilter !== 'all' ||
    eventSourceFilter !== 'all' ||
    exclusiveRetailerFilter !== 'all' ||
    boxNameFilter !== 'all' ||
    groupNameFilter !== 'all' ||
    charityNameFilter !== 'all' ||
    conventionNameFilter !== 'all' ||
    collabWithFilter !== 'all' ||
    collectionNameFilter !== 'all' ||
    yearFilter !== 'all' ||
    seasonFilter !== 'all' ||
    colorFamilyFilter !== 'all' ||
    completenessFilter !== 'all' ||
    tagFilter !== '';
  
  // Calculate counts for filters
  const allPolishes = allItems.filter(i => i.itemType === 'polish');
  const nailLacquers = allPolishes.filter(i => i.category === 'Nail Lacquer');
  const gelPolishes = allPolishes.filter(i => i.category === 'Gel Polish');
  const gelStampingPolishes = allPolishes.filter(i => i.category === 'Gel Stamping Polish');
  const dipPowders = allPolishes.filter(i => i.category === 'Dip Powder');
  const stampingPolishes = allPolishes.filter(i => i.category === 'Stamping Polish');
  const baseCoats = allPolishes.filter(i => i.category === 'Base Coat');
  const topCoats = allPolishes.filter(i => i.category === 'Top Coat');
  const builderGels = allPolishes.filter(i => i.category === 'Builder Gel');
  const polyGels = allPolishes.filter(i => i.category === 'Poly Gel');
  const treatments = allPolishes.filter(i => i.category === 'Treatment');
  const plates = allItems.filter(i => i.itemType === 'plate');
  const transfers = allItems.filter(i => i.itemType === 'transfer');
  const embellishments = allItems.filter(i => i.itemType === 'embellishment');
  const tools = allItems.filter(i => i.itemType === 'tool');
  const powders = allItems.filter(i => i.itemType === 'powder');
  const acrylics = allItems.filter(i => i.itemType === 'acrylic');
  const cuticleCares = allItems.filter(i => i.itemType === 'cuticle_care');
  
  // NEW: Calculate unique values for release filters
  // Event sources - include both legacy eventSource and new eventName across ALL
  // item types (not just polishes) so decals/plates/tools/etc. with an event
  // release show up in the filter dropdown. Exclude "Exclusive to X" items.
  const uniqueEventSources = [...new Set(allItems
    .flatMap(p => [(p as any).eventSource, (p as any).eventName])
    .filter((src): src is string => Boolean(src))
    .filter(src => !src.startsWith('Exclusive'))
  )].sort() as string[];


  // Exclusive retailers - include both legacy "Exclusive to X" and new exclusiveTo field
  const uniqueExclusiveRetailers = [...new Set(allPolishes
    .flatMap(p => {
      const results: string[] = [];
      // New structure: exclusiveTo field
      if (p.exclusiveTo) {
        results.push(p.exclusiveTo);
      }
      // Legacy structure: "Exclusive to X" in eventSource
      if (p.eventSource?.startsWith('Exclusive')) {
        if (p.eventSource === 'Exclusive') {
          results.push('Exclusive (unspecified)');
        } else {
          results.push(p.eventSource.replace('Exclusive to ', '').replace('Exclusive to by ', ''));
        }
      }
      return results;
    })
    .filter((src): src is string => Boolean(src))
  )].sort() as string[];

  const uniqueYears = [...new Set(allPolishes
    .map(p => p.releaseYear)
    .filter(Boolean)
  )].sort((a, b) => (b as number) - (a as number)) as number[];  // Newest first

  // Unique values for drill-down filters
  const uniqueBoxNames = [...new Set(allPolishes
    .map(p => (p as any).boxName)
    .filter((name): name is string => Boolean(name && name.trim()))
  )].sort() as string[];

  const uniqueGroupNames = [...new Set(allPolishes
    .map(p => (p as any).groupName)
    .filter((name): name is string => Boolean(name && name.trim()))
  )].sort() as string[];

  const uniqueCharityNames = [...new Set(allPolishes
    .map(p => (p as any).charityName)
    .filter((name): name is string => Boolean(name && name.trim()))
  )].sort() as string[];

  const uniqueConventionNames = [...new Set(allPolishes
    .map(p => (p as any).conventionName)
    .filter((name): name is string => Boolean(name && name.trim()))
  )].sort() as string[];

  const uniqueCollabNames = [...new Set(allPolishes
    .map(p => p.collabWith)
    .filter((name): name is string => Boolean(name && name.trim()))
  )].sort() as string[];

  const uniqueCollectionNames = [...new Set(allPolishes
    .map(p => p.collectionName)
    .filter((name): name is string => Boolean(name && name.trim()))
  )].sort() as string[];

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="glass-card p-8 text-center">
          <div className="inline-block w-12 h-12 border-4 border-slate-600 border-t-slate-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-glass-secondary font-body">Loading your collection...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 pb-32 md:pb-24">
      {/* Visibility Banner */}
      {username && (
        <PageVisibilityBanner 
          isPublic={isCollectionPublic}
          publicPath={`/profile/${username}/collection`}
          pageType="collection"
          className="mb-4"
        />
      )}

      {/* HEADER */}
      <div className="glass-card p-6 mb-4 text-center">
        <h1 className="text-3xl mb-1 text-glass font-heading">My Collection</h1>
        <p className="text-glass-secondary text-sm font-body">
          {sortedItems.length} of {allItems.filter(i => !i.isDeleted && !i.isDestashed).length} items
          {hasActiveFilters && <span className="text-glass ml-1">(filtered)</span>}
        </p>
        <div className="flex justify-center gap-4 mt-2 text-xs font-body">
          <button
            onClick={() => setArchiveFilter('in-collection')}
            className={`transition-default hover:text-glass ${archiveFilter === 'in-collection' ? 'text-glass underline underline-offset-2' : 'text-glass-secondary'}`}
          >
            <span className="font-semibold">
              {allItems.filter(i => !i.isDeleted && !i.isDestashed && !i.onWishlist).length}
            </span> Owned
          </button>
          <span className="text-glass-secondary opacity-40">•</span>
          <button
            onClick={() => setArchiveFilter('wishlist')}
            className={`transition-default hover:text-glass ${archiveFilter === 'wishlist' ? 'text-glass underline underline-offset-2' : 'text-glass-secondary'}`}
          >
            <span className="font-semibold">
              {allItems.filter(i => !i.isDeleted && !i.isDestashed && i.onWishlist).length}
            </span> Wishlist
          </button>
          <span className="text-glass-secondary opacity-40">•</span>
          <button
            onClick={() => setArchiveFilter('trash')}
            className={`transition-default hover:text-glass ${archiveFilter === 'trash' ? 'text-glass underline underline-offset-2' : 'text-glass-secondary'}`}
          >
            <span className="font-semibold">
              {allItems.filter(i => i.isDeleted).length}
            </span> Trash
          </button>
        </div>
        {allItems.length > 0 && (
          <Link 
            to="/import" 
            className="inline-flex items-center gap-1 text-xs text-glass hover:text-glass-secondary mt-2 transition-default font-body"
          >
            <Download className="icon-xs" />
            Import more items
          </Link>
        )}
      </div>

      {/* FILTER DRAWER */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowMobileFilters(false)}
          />
          {/* Drawer */}
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] glass-modal shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-glass-dark border-b border-slate-600 p-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-glass font-heading">Filters</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-slate-600/60 rounded-full transition-default text-glass-secondary hover:text-glass"
              >
                <X className="icon-md" />
              </button>
            </div>
            <div className="p-4">
              {/* Mobile filter content - same as sidebar */}
              {/* WISHLIST TOGGLE - At the very top */}
              <div className="mb-4">
                <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Wishlist Items</label>
                <button
                  onClick={() => setHideWishlistItems(!hideWishlistItems)}
                  disabled={archiveFilter === 'wishlist'}
                  className={`w-full px-3 py-2 border rounded-lg text-sm font-body text-left flex items-center justify-between ${
                    archiveFilter === 'wishlist' 
                      ? 'border-slate-700 bg-slate-800 text-slate-500 cursor-not-allowed' 
                      : hideWishlistItems 
                        ? 'border-amber-500 bg-amber-500/20 text-amber-300' 
                        : 'border-slate-600 text-glass-secondary hover:border-slate-500'
                  }`}
                  title={archiveFilter === 'wishlist' ? 'Cannot hide while viewing wishlist' : 'Toggle to hide/show wishlist items'}
                >
                  <span>{hideWishlistItems ? '⭐ Hidden' : '⭐ Shown'}</span>
                  <span className={`w-8 h-4 rounded-full relative transition-colors ${hideWishlistItems ? 'bg-amber-500' : 'bg-slate-600'}`}>
                    <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${hideWishlistItems ? 'left-4' : 'left-0.5'}`} />
                  </span>
                </button>
              </div>

              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-secondary" />
                  <input
                    type="text"
                    placeholder="Search name, brand, tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-600 bg-slate-800/40 text-glass rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm font-body placeholder-glass-muted"
                  />
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={() => clearAllFilters()}
                  className="w-full mb-4 px-3 py-2 bg-slate-600/80 text-white rounded-lg text-sm font-bold hover:bg-slate-600 transition-default flex items-center justify-center gap-2 font-body"
                >
                  <X className="icon-sm" /> Clear All Filters
                </button>
              )}

              {/* Categories */}
              <div className="mb-4">
                <button
                  onClick={() => toggleFilterSection('categories')}
                  className="w-full flex items-center justify-between py-2 text-sm font-bold text-glass-secondary font-body"
                >
                  Categories
                  {expandedFilterSections.has('categories') ? <ChevronUp className="icon-sm" /> : <ChevronDown className="icon-sm" />}
                </button>
                {expandedFilterSections.has('categories') && (
                  <div className="space-y-1 mt-2">
                    {/* All Items */}
                    <button
                      onClick={() => setMainFilter('all')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                        mainFilter === 'all'
                          ? 'bg-slate-700 text-white font-bold'
                          : 'hover:bg-slate-800/40 text-glass-secondary'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>All Items <span className="opacity-70">({allItems.length})</span></span>
                      </div>
                    </button>

                    {/* Acrylics */}
                    {acrylics.length > 0 && (
                      <button
                        onClick={() => setMainFilter('acrylic')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'acrylic'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Embellishment className="icon-sm" />
                          <span>Acrylics <span className="opacity-70">({acrylics.length})</span></span>
                        </div>
                      </button>
                    )}

                    {/* Cuticle Care */}
                    {cuticleCares.length > 0 && (
                      <button
                        onClick={() => setMainFilter('cuticle_care')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'cuticle_care'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.CuticleCare className="icon-sm" />
                          <span>Cuticle Care <span className="opacity-70">({cuticleCares.length})</span></span>
                        </div>
                      </button>
                    )}

                    {/* Embellishments */}
                    {embellishments.length > 0 && (
                      <button
                        onClick={() => setMainFilter('embellishment')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'embellishment'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Embellishment className="icon-sm" />
                          <span>Embellishments <span className="opacity-70">({embellishments.length})</span></span>
                        </div>
                      </button>
                    )}
                    {/* Embellishment Subcategories */}
                    {mainFilter === 'embellishment' && (
                      <div className="ml-3 pl-3 border-l-2 border-slate-600 space-y-1">
                        {[
                          { key: 'all', label: 'All', count: embellishments.length },
                          { key: 'Chains', label: 'Chains', count: embellishments.filter(e => e.embellishmentType === 'Chains').length },
                          { key: 'Charms', label: 'Charms', count: embellishments.filter(e => e.embellishmentType === 'Charms').length },
                          { key: 'Crystals', label: 'Crystals', count: embellishments.filter(e => e.embellishmentType === 'Crystals').length },
                          { key: 'Dried Flowers', label: 'Dried Flowers', count: embellishments.filter(e => e.embellishmentType === 'Dried Flowers').length },
                          { key: 'Foil', label: 'Foil', count: embellishments.filter(e => e.embellishmentType === 'Foil').length },
                          { key: 'Glitter', label: 'Glitter', count: embellishments.filter(e => e.embellishmentType === 'Glitter').length },
                          { key: 'Other', label: 'Other', count: embellishments.filter(e => e.embellishmentType === 'Other').length },
                          { key: 'Pearls', label: 'Pearls', count: embellishments.filter(e => e.embellishmentType === 'Pearls').length },
                          { key: 'Rhinestones', label: 'Rhinestones', count: embellishments.filter(e => e.embellishmentType === 'Rhinestones').length },
                          { key: 'Sequins', label: 'Sequins', count: embellishments.filter(e => e.embellishmentType === 'Sequins').length },
                          { key: 'Studs', label: 'Studs', count: embellishments.filter(e => e.embellishmentType === 'Studs').length },
                          { key: 'Wire', label: 'Wire', count: embellishments.filter(e => e.embellishmentType === 'Wire').length },
                        ].map(sub => (
                          <button
                            key={sub.key}
                            onClick={() => setEmbellishmentSubFilter(sub.key)}
                            className={`w-full text-left px-2 py-1 rounded text-xs transition-default font-body ${
                              embellishmentSubFilter === sub.key
                                ? `${CategoryColors.embellishment.badge} font-bold`
                                : 'hover:bg-slate-700 text-glass-secondary'
                            }`}
                          >
                            {sub.label} ({sub.count})
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Plates */}
                    {plates.length > 0 && (
                      <button
                        onClick={() => setMainFilter('plate')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'plate'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Plate className="icon-sm" />
                          <span>Plates <span className="opacity-70">({plates.length})</span></span>
                        </div>
                      </button>
                    )}
                    {/* Plate Subcategories */}
                    {mainFilter === 'plate' && (
                      <div className="ml-3 pl-3 border-l-2 border-slate-600 space-y-1">
                        {[
                          { key: 'all', label: 'All Plates', count: plates.length },
                          { key: 'Stamping Plate', label: 'Stamping Plate', count: plates.filter(p => p.plateType === 'Stamping Plate' || !p.plateType).length },
                          { key: 'Stencil', label: 'Stencil', count: plates.filter(p => p.plateType === 'Stencil').length },
                        ].map(sub => (
                          <button
                            key={sub.key}
                            onClick={() => setPlateSubFilter(sub.key)}
                            className={`w-full text-left px-2 py-1 rounded text-xs transition-default font-body ${
                              plateSubFilter === sub.key
                                ? `${CategoryColors.plate.badge} font-bold`
                                : 'hover:bg-slate-700 text-glass-secondary'
                            }`}
                          >
                            {sub.label} ({sub.count})
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Polish */}
                    {allPolishes.length > 0 && (
                      <button
                        onClick={() => setMainFilter('polish')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'polish'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Polish className="icon-sm" />
                          <span>Polish <span className="opacity-70">({allPolishes.length})</span></span>
                        </div>
                      </button>
                    )}
                    {/* Polish Subcategories */}
                    {mainFilter === 'polish' && (
                      <div className="ml-3 pl-3 border-l-2 border-slate-600 space-y-1">
                        {[
                          { key: 'all', label: 'All Polish', count: allPolishes.length },
                          { key: 'Base Coat', label: 'Base Coat', count: baseCoats.length },
                          { key: 'Builder Gel', label: 'Builder Gel', count: builderGels.length },
                          { key: 'Dip Powder', label: 'Dip Powder', count: dipPowders.length },
                          { key: 'Gel Polish', label: 'Gel Polish', count: gelPolishes.length },
                          { key: 'Gel Stamping Polish', label: 'Gel Stamping', count: gelStampingPolishes.length },
                          { key: 'Nail Lacquer', label: 'Nail Lacquer', count: nailLacquers.length },
                          { key: 'Poly Gel', label: 'Poly Gel', count: polyGels.length },
                          { key: 'Stamping Polish', label: 'Stamping', count: stampingPolishes.length },
                          { key: 'Top Coat', label: 'Top Coat', count: topCoats.length },
                          { key: 'Treatment', label: 'Treatment', count: treatments.length },
                        ].map(sub => (
                          <button
                            key={sub.key}
                            onClick={() => setPolishSubFilter(sub.key)}
                            className={`w-full text-left px-2 py-1 rounded text-xs transition-default font-body ${
                              polishSubFilter === sub.key
                                ? `${CategoryColors.polish.badge} font-bold`
                                : 'hover:bg-slate-700 text-glass-secondary'
                            }`}
                          >
                            {sub.label} ({sub.count})
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Powders */}
                    {powders.length > 0 && (
                      <button
                        onClick={() => setMainFilter('powder')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'powder'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Powder className="icon-sm" />
                          <span>Powders <span className="opacity-70">({powders.length})</span></span>
                        </div>
                      </button>
                    )}

                    {/* Tools */}
                    {tools.length > 0 && (
                      <button
                        onClick={() => setMainFilter('tool')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'tool'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Tool className="icon-sm" />
                          <span>Tools <span className="opacity-70">({tools.length})</span></span>
                        </div>
                      </button>
                    )}

                    {/* Transfers */}
                    {transfers.length > 0 && (
                      <button
                        onClick={() => setMainFilter('transfer')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-default font-body ${
                          mainFilter === 'transfer'
                            ? 'bg-slate-700 text-white font-bold'
                            : 'hover:bg-slate-800/40 text-glass-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AppIcons.Decal className="icon-sm" />
                          <span>Transfers <span className="opacity-70">({transfers.length})</span></span>
                        </div>
                      </button>
                    )}
                    {/* Transfer Subcategories */}
                    {mainFilter === 'transfer' && (
                      <div className="ml-3 pl-3 border-l-2 border-slate-600 space-y-1">
                        {[
                          { key: 'all', label: 'All Transfers', count: transfers.length },
                          { key: '3D Sticker', label: '3D Sticker', count: transfers.filter(t => (t.transferType as string) === '3D Stickers' || (t.transferType as string) === '3D Sticker').length },
                          { key: 'Foil', label: 'Foil', count: transfers.filter(t => t.transferType === 'Foil' || (t.transferType as string) === 'Transfer Foil' || (t.transferType as string) === 'Foil Transfer').length },
                          { key: 'Other', label: 'Other', count: transfers.filter(t => (t.transferType as string) === 'Other').length },
                          { key: 'Slider', label: 'Slider', count: transfers.filter(t => (t.transferType as string) === 'Sliders' || (t.transferType as string) === 'Slider').length },
                          { key: 'Sticker', label: 'Sticker', count: transfers.filter(t => t.transferType === 'Sticker' || (t.transferType as string) === 'Stickers' || (t.transferType as string) === 'Nail Stickers').length },
                          { key: 'Tattoo', label: 'Tattoo', count: transfers.filter(t => (t.transferType as string) === 'Tattoos' || (t.transferType as string) === 'Tattoo').length },
                          { key: 'Vinyl', label: 'Vinyl', count: transfers.filter(t => (t.transferType as string) === 'Vinyls' || (t.transferType as string) === 'Vinyl').length },
                          { key: 'Waterslide Decal', label: 'Waterslide Decal', count: transfers.filter(t => t.transferType === 'Waterslide Decal' || (t.transferType as string) === 'Water Decal' || (t.transferType as string) === 'Waterslide Decals' || (t.transferType as string) === 'Water Decals').length },
                          { key: 'Wrap', label: 'Wrap', count: transfers.filter(t => (t.transferType as string) === 'Wrap' || (t.transferType as string) === 'Wraps' || (t.transferType as string) === 'Nail Wrap').length },
                        ].map(sub => (
                          <button
                            key={sub.key}
                            onClick={() => setTransferSubFilter(sub.key)}
                            className={`w-full text-left px-2 py-1 rounded text-xs transition-default font-body ${
                              transferSubFilter === sub.key
                                ? `${CategoryColors.transfer.badge} font-bold`
                                : 'hover:bg-slate-700 text-glass-secondary'
                            }`}
                          >
                            {sub.label} ({sub.count})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sort & Show */}
              <div className="mb-4 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body"
                  >
                    <option value="brand-name">Brand → Name</option>
                    <option value="name-only">Name only</option>
                    <option value="most-recent">Recently added</option>
                    <option value="release-newest">Newest release</option>
                    <option value="release-oldest">Oldest release</option>
                    <option value="most-used">Most used</option>
                    <option value="least-used">Least used</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Show</label>
                  <select
                    value={archiveFilter}
                    onChange={(e) => setArchiveFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body"
                  >
                    <option value="in-collection">✨ In Collection</option>
                    <option value="destashed">📦 Archived/Destashed</option>
                    <option value="trash">🗑️ Trash</option>
                    <option value="all">📋 All Items</option>
                    <option value="favorites">❤️ Favorites</option>
<option value="wishlist">⭐ Wishlist Items</option>
                    <option value="rare-le">💎 Rare/Limited Edition</option>
                    <option value="for-destash">🏷️ For Destash</option>
                    <option value="open-to-swaps">🔄 Open to Swaps</option>
                    <option value="never-used">❄️ Never Used</option>
                    <option value="has-notes">📝 Has Notes</option>
                    <option value="swatched">🎨 Swatched</option>
                    <option value="not-swatched">⬜ Not Swatched</option>
                    <option value="needs-info">ℹ️ Needs Info</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Brand</label>
                  <select
                    value={brandFilter}
                    onChange={(e) => {
                      setBrandFilter(e.target.value);
                      // Reset collection/event filter when brand changes
                      setCollectionEventFilter('all');
                    }}
                    className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body"
                  >
                    <option value="all">All brands</option>
                    {uniqueBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
                {/* Collection/Event Filter - shows available collections/events for selected brand */}
                {uniqueCollectionsEventsList.length > 0 && (
                  <div>
                    <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Collection / Event</label>
                    <select
                      value={collectionEventFilter}
                      onChange={(e) => setCollectionEventFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body"
                    >
                      <option value="all">All collections/events</option>
                      {uniqueCollectionsEventsList.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Release Filters - Moved above More Filters */}
              <div className="mb-4">
                <button
                  onClick={() => toggleFilterSection('release')}
                  className="w-full flex items-center justify-between py-2 text-sm font-bold text-glass-secondary font-body"
                >
                  Release Filters
                  {(releaseTypeFilter !== 'all' || eventSourceFilter !== 'all' || exclusiveRetailerFilter !== 'all' || boxNameFilter !== 'all' || groupNameFilter !== 'all' || charityNameFilter !== 'all' || conventionNameFilter !== 'all' || collabWithFilter !== 'all' || collectionNameFilter !== 'all' || yearFilter !== 'all' || seasonFilter !== 'all') && (
                    <span className="w-2 h-2 bg-slate-500 rounded-full mr-2"></span>
                  )}
                  {expandedFilterSections.has('release') ? <ChevronUp className="icon-sm" /> : <ChevronDown className="icon-sm" />}
                </button>
                {expandedFilterSections.has('release') && (
                  <div className="space-y-3 mt-2">
                    <div>
                      <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Release Type</label>
                      <select 
                        value={releaseTypeFilter} 
                        onChange={(e) => {
                          setReleaseTypeFilter(e.target.value);
                          // Reset all sub-filters when changing type
                          setEventSourceFilter('all');
                          setExclusiveRetailerFilter('all');
                          setBoxNameFilter('all');
                          setGroupNameFilter('all');
                          setCharityNameFilter('all');
                          setConventionNameFilter('all');
                          setCollabWithFilter('all');
                          setCollectionNameFilter('all');
                        }} 
                        className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body"
                      >
                        <option value="all">All types</option>
                        <option value="core">🏠 Core / Permanent</option>
                        <option value="collection">📚 Collection</option>
                        <option value="event">🎪 Event</option>
                        <option value="box">📦 Box</option>
                        <option value="groupCustom">👥 Group Custom</option>
                        <option value="exclusive">⭐ Exclusive</option>
                        <option value="charity">💝 Charity</option>
                        <option value="limitedEdition">✨ Limited Edition</option>
                        <option value="convention">🎫 Convention</option>
                        <option value="collab">🤝 Collab</option>
                        <option value="other">📝 Other</option>
                        <option value="unknown">❓ Unknown</option>
                      </select>
                    </div>
                    
                    {/* Collection Name - shows for collection type */}
                    {releaseTypeFilter === 'collection' && uniqueCollectionNames.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Collection Name</label>
                        <select value={collectionNameFilter} onChange={(e) => setCollectionNameFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All collections</option>
                          {uniqueCollectionNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Event Source - shows for event type */}
                    {releaseTypeFilter === 'event' && uniqueEventSources.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Event Name</label>
                        <select value={eventSourceFilter} onChange={(e) => setEventSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All events</option>
                          {uniqueEventSources.map(src => (
                            <option key={src} value={src}>{src}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Box Name - shows for box type */}
                    {releaseTypeFilter === 'box' && uniqueBoxNames.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Box Name</label>
                        <select value={boxNameFilter} onChange={(e) => setBoxNameFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All boxes</option>
                          {uniqueBoxNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Group Name - shows for groupCustom type */}
                    {releaseTypeFilter === 'groupCustom' && uniqueGroupNames.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Group Name</label>
                        <select value={groupNameFilter} onChange={(e) => setGroupNameFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All groups</option>
                          {uniqueGroupNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Exclusive Retailer - shows for exclusive type */}
                    {releaseTypeFilter === 'exclusive' && uniqueExclusiveRetailers.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Exclusive To</label>
                        <select value={exclusiveRetailerFilter} onChange={(e) => setExclusiveRetailerFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All retailers</option>
                          {uniqueExclusiveRetailers.map(retailer => (
                            <option key={retailer} value={retailer}>{retailer}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Charity Name - shows for charity type */}
                    {releaseTypeFilter === 'charity' && uniqueCharityNames.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Charity Name</label>
                        <select value={charityNameFilter} onChange={(e) => setCharityNameFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All charities</option>
                          {uniqueCharityNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Convention Name - shows for convention type */}
                    {releaseTypeFilter === 'convention' && uniqueConventionNames.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Convention Name</label>
                        <select value={conventionNameFilter} onChange={(e) => setConventionNameFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All conventions</option>
                          {uniqueConventionNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Collab With - shows for collab type */}
                    {releaseTypeFilter === 'collab' && uniqueCollabNames.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Collab With</label>
                        <select value={collabWithFilter} onChange={(e) => setCollabWithFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All collaborators</option>
                          {uniqueCollabNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {uniqueYears.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Year</label>
                        <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All years</option>
                          {uniqueYears.map(yr => (
                            <option key={yr} value={yr.toString()}>{yr}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Season</label>
                      <select value={seasonFilter} onChange={(e) => setSeasonFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                        <option value="all">All seasons</option>
                        <option value="Spring">🌷 Spring</option>
                        <option value="Summer">☀️ Summer</option>
                        <option value="Fall">🍂 Fall</option>
                        <option value="Winter">❄️ Winter</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* More Filters - Now below Release Filters */}
              <div className="mb-4">
                <button
                  onClick={() => toggleFilterSection('more')}
                  className="w-full flex items-center justify-between py-2 text-sm font-bold text-glass-secondary font-body"
                >
                  More Filters
                  {hasMoreFiltersActive && <span className="w-2 h-2 bg-slate-500 rounded-full mr-2"></span>}
                  {expandedFilterSections.has('more') ? <ChevronUp className="icon-sm" /> : <ChevronDown className="icon-sm" />}
                </button>
                {expandedFilterSections.has('more') && (
                  <div className="space-y-3 mt-2">
                    {/* Tag Filter */}
                    <div>
                      <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Tag</label>
                      <select 
                        value={tagFilter} 
                        onChange={(e) => setTagFilter(e.target.value)} 
                        className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body"
                      >
                        <option value="">All tags</option>
                        <optgroup label="🎨 Base Type">
                          <option value="baseType:creme">Creme</option>
                          <option value="baseType:crelly">Crelly</option>
                          <option value="baseType:jelly">Jelly</option>
                          <option value="baseType:sheer">Sheer</option>
                        </optgroup>
                        <optgroup label="✨ Finish">
                          <option value="finish:shimmer">Shimmer</option>
                          <option value="finish:metallic">Metallic</option>
                          <option value="finish:chrome">Chrome</option>
                          <option value="finish:matte">Matte</option>
                          <option value="finish:satin">Satin</option>
                          <option value="finish:iridescent">Iridescent</option>
                        </optgroup>
                        <optgroup label="🌈 Holo">
                          <option value="holoType:linear holo">Linear Holo</option>
                          <option value="holoType:scattered holo">Scattered Holo</option>
                          <option value="holoType:holo glitter">Holo Glitter</option>
                        </optgroup>
                        <optgroup label="🔀 Color Shift">
                          <option value="colorShift:duochrome">Duochrome</option>
                          <option value="colorShift:multichrome">Multichrome</option>
                        </optgroup>
                        <optgroup label="💎 Particles">
                          <option value="particles:glitter">Glitter</option>
                          <option value="particles:flakes">Flakes</option>
                          <option value="particles:aurora pigment">Aurora Pigment</option>
                        </optgroup>
                        <optgroup label="⚡ Special">
                          <option value="specialFeatures:magnetic">Magnetic</option>
                          <option value="specialFeatures:thermal">Thermal</option>
                          <option value="specialFeatures:glow-in-the-dark">Glow in Dark</option>
                          <option value="specialFeatures:solar">Solar</option>
                        </optgroup>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Color</label>
                      <select value={colorFamilyFilter} onChange={(e) => setColorFamilyFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                        <option value="all">All colors</option>
                        <option value="red">❤️ Red</option>
                        <option value="orange">🧡 Orange</option>
                        <option value="yellow">💛 Yellow</option>
                        <option value="green">💚 Green</option>
                        <option value="blue">💙 Blue</option>
                        <option value="purple">💜 Purple</option>
                        <option value="pink">🩷 Pink</option>
                        <option value="neutral">🤍 Neutral</option>
                        <option value="brown">🤎 Brown</option>
                        <option value="black">🖤 Black</option>
                        <option value="white">⬜ White</option>
                        <option value="gold">⭐ Gold</option>
                        <option value="silver">🩶 Silver</option>
                        <option value="copper">🔶 Copper</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Missing</label>
                      <select value={completenessFilter} onChange={(e) => setCompletenessFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                        <option value="all">Show all</option>
                        <option value="no-photo">📷 No photo</option>
                        <option value="no-tags">🏷️ No tags</option>
                        <option value="no-storage">📍 No storage</option>
                        <option value="no-color-family">🎨 No color family</option>
                        <option value="no-release">📅 No release info</option>
                        <option value="no-description">📝 No description</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Quantity</label>
                      <select value={quantityFilter} onChange={(e) => setQuantityFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                        <option value="any">Any quantity</option>
                        <option value="multiples">📦 2+ (Multiples)</option>
                        <option value="single">1️⃣ Exactly 1</option>
                        <option value="none">⭕ Out of stock (0)</option>
                      </select>
                    </div>
                    {uniqueStorageLocations.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-glass-muted mb-1 font-body">Storage</label>
                        <select value={storageFilter} onChange={(e) => setStorageFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm font-body">
                          <option value="all">All locations</option>
                          {uniqueStorageLocations.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Search by Color */}
              <div className="mb-4 pt-4 border-t border-slate-600">
                <ColorSearchFilter
                  selectedColor={searchColor}
                  onColorSelect={setSearchColor}
                  similarity={colorSimilarity}
                  onSimilarityChange={setColorSimilarity}
                />
                </div>

              {/* Apply button */}
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg font-bold hover:bg-slate-800 transition-default font-body"
              >
                Show {sortedItems.length} Results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="relative">
        {/* FLOATING ACTION BUTTONS - Top Right */}
        {/* Desktop: Side buttons (hidden on mobile) */}
        <div className="hidden md:flex fixed top-20 right-4 z-40 flex-col gap-2">
          {/* Filter Button */}
          <button
            onClick={() => setShowMobileFilters(true)}
            className={`w-12 h-12 rounded-full font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-105 ${
              hasActiveFilters
                ? 'bg-slate-500/90 hover:bg-slate-400/90 text-white'
                : 'bg-slate-700/90 text-white hover:bg-slate-600/90'
            }`}
            title="Filters & Search"
          >
            <SlidersHorizontal className="icon-md" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                !
              </span>
            )}
          </button>

          {/* Add Item Button */}
          <button
            onClick={() => {
              if (mainFilter === 'all') {
                setShowItemPicker(true);
              } else {
                setShowAddModal(true);
              }
            }}
            className="w-12 h-12 bg-slate-600/90 text-white rounded-full font-bold hover:bg-slate-500/90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-105"
            title="Add New Item"
          >
            <Plus className="icon-base" />
          </button>
          
          {/* Select Items Button */}
          <button
            onClick={toggleSelectionMode}
            className={`w-12 h-12 rounded-full font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-105 ${
              isSelectionMode
                ? 'bg-slate-500/90 hover:bg-slate-400/90 text-white'
                : 'bg-slate-700/90 text-white hover:bg-slate-600/90'
            }`}
            title={isSelectionMode ? "Exit Selection Mode" : "Select Items"}
          >
            {isSelectionMode ? (
              <CheckSquare className="icon-md" />
            ) : (
              <Square className="icon-md" />
            )}
          </button>

          {/* View Toggle - Grid */}
          <button
            onClick={() => setViewMode('grid')}
            className={`w-12 h-12 rounded-full transition-all shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-105 ${
              viewMode === 'grid'
                ? 'bg-slate-500/90 hover:bg-slate-400/90 text-white'
                : 'bg-slate-700/90 text-white hover:bg-slate-600/90'
            }`}
            title="Grid View"
          >
            <Grid className="icon-md" />
          </button>

          {/* View Toggle - List */}
          <button
            onClick={() => setViewMode('list')}
            className={`w-12 h-12 rounded-full transition-all shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-105 ${
              viewMode === 'list'
                ? 'bg-slate-500/90 hover:bg-slate-400/90 text-white'
                : 'bg-slate-700/90 text-white hover:bg-slate-600/90'
            }`}
            title="List View"
          >
            <List className="icon-md" />
          </button>

          {/* Items per page */}
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="w-12 h-12 bg-slate-700/90 border-2 border-slate-500/50 rounded-full text-sm text-center shadow-lg hover:shadow-xl hover:bg-slate-600/90 appearance-none cursor-pointer font-bold text-white font-body"
            title="Items per page"
          >
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={96}>96</option>
          </select>
        </div>

        {/* Mobile: Bottom toolbar (hidden on desktop, hidden during selection mode) */}
        {!isSelectionMode && (
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-glass-dark border-t border-slate-600 px-2 pt-2 pb-3">
            <div className="flex items-center justify-around gap-1">
              {/* Filter Button */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className={`relative flex-1 max-w-16 h-12 rounded-xl font-bold transition-all flex flex-col items-center justify-center gap-0.5 ${
                  hasActiveFilters
                    ? 'bg-slate-500 text-white'
                    : 'bg-slate-700/80 text-white'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
                <span className="text-[10px]">Filter</span>
                {hasActiveFilters && (
                  <span className="absolute top-1 right-2 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>

              {/* Add Item Button */}
              <button
                onClick={() => {
                  if (mainFilter === 'all') {
                    setShowItemPicker(true);
                  } else {
                    setShowAddModal(true);
                  }
                }}
                className="flex-1 max-w-16 h-12 bg-indigo-600 text-white rounded-xl font-bold transition-all flex flex-col items-center justify-center gap-0.5"
              >
                <Plus className="w-5 h-5" />
                <span className="text-[10px]">Add</span>
              </button>
              
              {/* Select Items Button */}
              <button
                onClick={toggleSelectionMode}
                className="flex-1 max-w-16 h-12 bg-slate-700/80 text-white rounded-xl font-bold transition-all flex flex-col items-center justify-center gap-0.5"
              >
                <Square className="w-5 h-5" />
                <span className="text-[10px]">Select</span>
              </button>

              {/* View Toggle */}
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className={`flex-1 max-w-16 h-12 rounded-xl font-bold transition-all flex flex-col items-center justify-center gap-0.5 ${
                  viewMode === 'grid' ? 'bg-slate-600 text-white' : 'bg-slate-700/80 text-white'
                }`}
              >
                {viewMode === 'grid' ? <Grid className="w-5 h-5" /> : <List className="w-5 h-5" />}
                <span className="text-[10px]">{viewMode === 'grid' ? 'Grid' : 'List'}</span>
              </button>

              {/* Items per page */}
              <div className="flex-1 max-w-16 h-12 bg-slate-700/80 rounded-xl flex flex-col items-center justify-center relative">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full"
                >
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                  <option value={96}>96</option>
                </select>
                <span className="text-white font-bold text-sm">{itemsPerPage}</span>
                <span className="text-[10px] text-white">Per Page</span>
              </div>
            </div>
          </div>
        )}

        {/* Selection Mode Floating Bar - Shows selected count and quick actions */}
          {isSelectionMode && (
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-40 glass-card rounded-2xl shadow-xl px-3 sm:px-4 py-2 flex flex-wrap items-center justify-center gap-2 sm:gap-3 max-w-[95vw]">
              <span className="text-sm font-bold text-glass font-body">
                {selectedItems.size} selected
              </span>
              <div className="h-4 w-px bg-slate-600 hidden sm:block"></div>
              
              {/* Select All on Page */}
              <button
                onClick={handleSelectAllPage}
                className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold transition-default font-body ${
                  selectedItems.size === paginatedItems.length && paginatedItems.length > 0
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-600/80 text-white hover:bg-blue-600'
                }`}
                title={selectedItems.size === paginatedItems.length ? "Deselect all on this page" : "Select all items on this page"}
              >
                <span className="hidden sm:inline">
                  {selectedItems.size === paginatedItems.length && paginatedItems.length > 0 
                    ? `Page Selected (${paginatedItems.length})` 
                    : `Select Page (${paginatedItems.length})`}
                </span>
                <span className="sm:hidden">
                  Page ({paginatedItems.length})
                </span>
              </button>

              {/* Select All Filtered - Always show if there are more filtered items than on page */}
              {sortedItems.length > 0 && (
                <button
                  onClick={handleSelectAllFiltered}
                  className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold transition-default font-body ${
                    selectedItems.size === sortedItems.length
                      ? 'bg-purple-500 text-white'
                      : 'bg-purple-600/80 text-white hover:bg-purple-600'
                  }`}
                  title={selectedItems.size === sortedItems.length ? "Deselect all filtered items" : "Select all items matching your filters"}
                >
                  <span className="hidden sm:inline">
                    {selectedItems.size === sortedItems.length 
                      ? `All Filtered Selected (${sortedItems.length})` 
                      : `Select All Filtered (${sortedItems.length})`}
                  </span>
                  <span className="sm:hidden">
                    All ({sortedItems.length})
                  </span>
                </button>
              )}

              {selectedItems.size > 0 && (
                <button
                  onClick={() => setSelectedItems(new Set())}
                  className="px-2 sm:px-3 py-1 bg-slate-600/80 text-white rounded-full text-xs font-bold hover:bg-slate-600 transition-default font-body"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Results Count */}
          <div className="glass-card px-4 py-2 mb-4 text-center">
            <p className="text-sm text-glass font-bold font-body">
              Showing {Math.min(startIndex + 1, sortedItems.length)}-{Math.min(endIndex, sortedItems.length)} of {sortedItems.length} items
              {totalPages > 1 && <span className="text-glass-secondary"> • Page {currentPage} of {totalPages}</span>}
            </p>
          </div>

          {/* ITEMS - GRID OR LIST VIEW */}
          {paginatedItems.length === 0 ? (
        <div className="glass-card p-12 text-center mb-8">
          {allItems.length === 0 ? (
            <>
              <div className="text-6xl mb-4">💅</div>
              <h3 className="text-2xl font-bold mb-2 font-heading">Your Collection is Empty</h3>
              <p className="text-glass-secondary mb-6 font-body">
                Start building your nail art collection! Add items one at a time, or import from a spreadsheet.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setShowItemPicker(true)}
                  className="px-8 py-4 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition-default shadow-lg text-lg font-body"
                >
                  + Add Your First Item
                </button>
                <Link 
                  to="/import"
                  className="px-8 py-4 bg-slate-700 text-white rounded-full font-bold hover:bg-slate-800 transition-default shadow-lg text-lg inline-flex items-center justify-center gap-2 font-body"
                >
                  <Download className="icon-md" />
                  Import from Spreadsheet
                </Link>
              </div>
              <p className="text-glass-muted text-xs mt-4 font-body">
                Already have some items? No worries — importing will skip any duplicates.
              </p>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold mb-2 font-heading">No Items Found</h3>
              <p className="text-glass-secondary mb-4 font-body">
                Try adjusting your filters or search terms.
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-6 py-3 bg-slate-700 text-white rounded-full font-bold hover:bg-slate-800 transition-default font-body"
                >
                  Clear All Filters
                </button>
              )}
            </>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* GRID VIEW */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {paginatedItems.map((item) => {
            // Use PolishCard component for polish items
            if (item.itemType === 'polish') {
              return (
                <PolishCard
                  key={item.id}
                  polish={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            // Use PlateCard component for plate items
            if (item.itemType === 'plate') {
              return (
                <PlateCard
                  key={item.id}
                  plate={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            //  Use EmbellishmentCard for embellishment items
            if (item.itemType === 'embellishment') {
              return (
              <EmbellishmentCard
              key={item.id}
              embellishment={item}
              userId={auth.currentUser?.uid || ''}
              onUpdate={async (_id, updates) => {
                const itemToUpdate = allItems.find(i => i.id === item.id);
                if (!itemToUpdate) return;
                const updatedItem = { ...itemToUpdate, ...updates };
                await handleSaveItem(updatedItem);
              }}
              onClick={() => {
                if (!isSelectionMode) {
                  const freshItem = allItems.find(i => i.id === item.id);
                  setSelectedPolish(freshItem || item);
                }
              }}
              isSelectionMode={isSelectionMode}
              isSelected={selectedItems.has(item.id)}
              onSelect={handleSelectItem}
              onFilterByTag={handleTagFilterClick}
              onFilterByCollection={handleCollectionFilterClick}
              onFilterByEvent={handleEventFilterClick}
              />
              );
            }

            // Use DecalCard component for transfer/decal items
            if (item.itemType === 'transfer') {
              return (
                <DecalCard
                  key={item.id}
                  decal={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            // Use ToolCard component for tool items
            if (item.itemType === 'tool') {
              return (
                <ToolCard
                  key={item.id}
                  tool={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            // Use BuffingPowderCard component for powder items
            if (item.itemType === 'powder') {
              return (
                <BuffingPowderCard
                  key={item.id}
                  powder={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            // Use AcrylicCard component for acrylic items
            if (item.itemType === 'acrylic') {
              return (
                <AcrylicCard
                  key={item.id}
                  acrylic={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            // Use CuticleCareCard component for cuticle care items
            if (item.itemType === 'cuticle_care') {
              return (
                <CuticleCareCard
                  key={item.id}
                  cuticleItem={item}
                  userId={auth.currentUser?.uid || ''}
                  onUpdate={async (_id, updates) => {
                    const itemToUpdate = allItems.find(i => i.id === item.id);
                    if (!itemToUpdate) return;
                    const updatedItem = { ...itemToUpdate, ...updates };
                    await handleSaveItem(updatedItem);
                  }}
                  onClick={() => {
                    if (!isSelectionMode) {
                      const freshItem = allItems.find(i => i.id === item.id);
                      setSelectedPolish(freshItem || item);
                    }
                  }}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                  onFilterByTag={handleTagFilterClick}
                  onFilterByCollection={handleCollectionFilterClick}
                  onFilterByEvent={handleEventFilterClick}
                />
              );
            }

            // For other item types, render generic card
            return (
            <div
              key={item.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all"
            >
              {/* Photo */}
              {(() => {
                const photoUrl = getItemPhoto(item);
                return photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={`${item.brand} ${item.name}`}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 rainbow-gradient flex items-center justify-center text-6xl">
                    💅
                  </div>
                );
              })()}
              
              {/* Info */}
              <div className="p-4">
                <h3 className="text-xs uppercase text-glass font-bold tracking-wide mb-1 font-heading">
                  {item.brand}
                </h3>
                
                {/* Clickable name */}
                <button
                  onClick={() => setSelectedPolish(item)}
                  className="w-full text-left text-lg font-bold text-glass mb-2 hover:text-glass-secondary transition-default font-body"
                >
                  {item.name}
                </button>
                
                {/* Color Description */}
                {item.colorDescription && (
                  <p className="text-sm text-glass-secondary mb-2 line-clamp-2 font-body">
                    {item.colorDescription}
                  </p>
                )}
                
                {/* Quantity */}
                {item.quantity > 1 && (
                  <span className="inline-block px-3 py-1 bg-yellow-400 text-glass rounded-full text-xs font-bold font-body">
                    Qty: {item.quantity}
                  </span>
                )}
                
                {/* Destash badge */}
                {item.isForDestash && (
                  <span className="inline-block ml-2 px-3 py-1 bg-orange-500 text-white rounded-full text-xs font-bold font-body">
                    🛍️ Destash
                  </span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        /* LIST VIEW */
        <div className="glass-card overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/60 border-b-2 border-slate-600">
                <tr>
                  {isSelectionMode && (
                    <th className="px-4 py-3 text-center w-12">
                      <button
                        onClick={handleSelectAllPage}
                        className="p-1 hover:bg-slate-600/60 rounded transition-default"
                        title={selectedItems.size === paginatedItems.length ? "Deselect all on this page" : "Select all on this page"}
                      >
                        {selectedItems.size === paginatedItems.length ? (
                          <CheckSquare className="icon-md text-glass" />
                        ) : (
                          <Square className="icon-md text-glass-muted" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-bold text-glass-secondary font-body">Photo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-glass-secondary font-body">Brand</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-glass-secondary font-body">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-glass-secondary font-body">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-glass-secondary font-body">Tags</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-glass-secondary font-body">Qty</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-glass-secondary font-body">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`border-b border-slate-600 hover:bg-slate-600/60 transition-default cursor-pointer ${
                      index % 2 === 0 ? 'bg-slate-800/40' : 'bg-slate-800/60'
                    } ${selectedItems.has(item.id) ? 'bg-slate-500/70 hover:bg-slate-500/80' : ''}`}
                    onClick={() => {
                      if (isSelectionMode) {
                        handleSelectItem(item.id);
                      } else {
                        setSelectedPolish(item);
                      }
                    }}
                  >
                    {isSelectionMode && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleSelectItem(item.id)}
                          className="p-1 hover:bg-slate-600/60 rounded transition-default"
                        >
                          {selectedItems.has(item.id) ? (
                            <CheckSquare className="icon-md text-glass" />
                          ) : (
                            <Square className="icon-md text-glass-muted" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {(() => {
                        const photoUrl = getItemPhoto(item);
                        return photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 rainbow-gradient rounded flex items-center justify-center text-2xl">
                            💅
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-glass font-heading">
                      {item.brand}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-sm font-bold text-glass font-body"
                      >
                        {item.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-glass-secondary font-body">
                      {item.itemType === 'polish' && item.category}
                      {item.itemType === 'transfer' && item.transferType}
                      {item.itemType === 'embellishment' && item.embellishmentType}
                      {item.itemType === 'plate' && 'Stamping Plate'}
                      {item.itemType === 'tool' && 'Tool'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {/* Base Type */}
                        {item.baseType && (
                          <button
                            onClick={() => handleTagFilterClick('baseType', item.baseType!)}
                            className="px-2 py-1 bg-blue-600/80 text-white rounded text-xs font-bold hover:bg-blue-600 transition-default cursor-pointer font-body"
                            title={`Filter by ${item.baseType}`}
                          >
                            {item.baseType}
                          </button>
                        )}
                        {/* Finish */}
                        {item.finish && item.finish.length > 0 && (
                          <button
                            onClick={() => handleTagFilterClick('finish', item.finish![0])}
                            className="px-2 py-1 bg-purple-600/80 text-white rounded text-xs font-bold hover:bg-purple-600 transition-default cursor-pointer font-body"
                            title={`Filter by ${item.finish[0]}`}
                          >
                            {item.finish[0]}
                          </button>
                        )}
                        {/* Holo Type */}
                        {item.holoType && item.holoType.length > 0 && (
                          <button
                            onClick={() => handleTagFilterClick('holoType', item.holoType![0])}
                            className="px-2 py-1 bg-pink-600/80 text-white rounded text-xs font-bold hover:bg-pink-600 transition-default cursor-pointer font-body"
                            title={`Filter by ${item.holoType[0]}`}
                          >
                            {item.holoType[0]}
                          </button>
                        )}
                        {/* Color Shift */}
                        {item.colorShift && (
                          <button
                            onClick={() => handleTagFilterClick('colorShift', item.colorShift!)}
                            className={`px-2 py-1 ${CategoryColors.polish.bgMuted} text-white rounded text-xs font-bold hover:${CategoryColors.polish.bg} transition-default cursor-pointer font-body`}
                            title={`Filter by ${item.colorShift}`}
                          >
                            {item.colorShift}
                          </button>
                        )}
                        {/* Particles (first one) */}
                        {item.particles && item.particles.length > 0 && (
                          <button
                            onClick={() => handleTagFilterClick('particles', item.particles![0])}
                            className={`px-2 py-1 ${CategoryColors.tool.bgMuted} text-white rounded text-xs font-bold hover:${CategoryColors.tool.bg} transition-default cursor-pointer font-body`}
                            title={`Filter by ${item.particles[0]}`}
                          >
                            {item.particles[0]}
                          </button>
                        )}
                        {/* Special Features (first one) */}
                        {item.specialFeatures && item.specialFeatures.length > 0 && (
                          <button
                            onClick={() => handleTagFilterClick('specialFeatures', item.specialFeatures![0])}
                            className="px-2 py-1 bg-green-600/80 text-white rounded text-xs font-bold hover:bg-green-600 transition-default cursor-pointer font-body"
                            title={`Filter by ${item.specialFeatures[0]}`}
                          >
                            {item.specialFeatures[0]}
                          </button>
                        )}
                        {/* Plate Image Tags (show first 3) */}
                        {item.itemType === 'plate' && item.imageTags && item.imageTags.length > 0 && (
                          <>
                            {item.imageTags.slice(0, 3).map((tag, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagFilterClick('imageTags', tag);
                                }}
                                className="px-2 py-1 bg-purple-600/80 text-white rounded text-xs font-bold hover:bg-purple-600 transition-default cursor-pointer font-body"
                                title={`Filter by ${tag}`}
                              >
                                {tag}
                              </button>
                            ))}
                            {item.imageTags.length > 3 && (
                              <span className="px-2 py-1 bg-slate-600/60 text-glass-muted rounded text-xs font-body">
                                +{item.imageTags.length - 3}
                              </span>
                            )}
                          </>
                        )}
                        {/* Generic Tags (for items that have them) */}
                        {item.tags && item.tags.length > 0 && (
                          <>
                            {item.tags.slice(0, 2).map((tag, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagFilterClick('tags', tag);
                                }}
                                className="px-2 py-1 bg-slate-500/80 text-white rounded text-xs font-bold hover:bg-slate-500 transition-default cursor-pointer font-body"
                                title={`Filter by ${tag}`}
                              >
                                {tag}
                              </button>
                            ))}
                            {item.tags.length > 2 && (
                              <span className="px-2 py-1 bg-slate-600/60 text-glass-muted rounded text-xs font-body">
                                +{item.tags.length - 2}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-glass font-body">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col gap-1 items-center">
                        {item.onWishlist && <span className="text-xs">⭐</span>}
                        {item.isForDestash && <span className="text-xs">🛍️</span>}
                        {item.isOpenToSwaps && <span className="text-xs">🔄</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="glass-card p-4 mb-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {/* First Page Button */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-slate-800/60 text-glass-secondary rounded-lg hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-default font-body"
                title="First Page"
              >
                ⏮️
              </button>

              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-slate-800/60 text-glass-secondary rounded-lg hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-default font-body"
              >
                ← Prev
              </button>
              
              {/* Smart Page Numbers */}
              <div className="flex gap-1 items-center">
                {/* Always show page 1 */}
                {currentPage > 3 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="w-10 h-10 rounded-lg font-bold transition-default bg-slate-800/60 text-glass-secondary hover:bg-white/80 font-body"
                    >
                      1
                    </button>
                    {currentPage > 4 && (
                      <span className="px-2 text-glass-secondary text-lg font-body">...</span>
                    )}
                  </>
                )}

                {/* Pages around current page */}
                {[...Array(5)].map((_, i) => {
                  const pageNum = currentPage - 2 + i;
                  if (pageNum < 1 || pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-bold transition-default font-body ${
                        currentPage === pageNum
                          ? 'bg-slate-700 text-white shadow-md'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {/* Always show last page */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <span className="px-2 text-glass-secondary text-lg font-body">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="w-10 h-10 rounded-lg font-bold transition-default bg-slate-800/60 text-glass-secondary hover:bg-white/80 font-body"
                    >
                      {totalPages}
                  </button>
                </>
              )}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-slate-800/60 text-glass-secondary rounded-lg hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-default font-body"
            >
              Next →
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-slate-800/60 text-glass-secondary rounded-lg hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-default font-body"
              title="Last Page"
            >
              ⏭️
            </button>
          </div>

          {/* Page info and Go To input */}
          <div className="flex items-center justify-center gap-4">
            <span className="text-glass text-sm font-bold font-body">
              Page {currentPage} of {totalPages}
            </span>

            {/* Go to page input */}
            <div className="flex items-center gap-2">
              <span className="text-glass text-sm font-body">
                Go to:
              </span>
              <input
                type="number"
                min="1"
                max={totalPages}
                placeholder="#"
                className="w-16 px-2 py-1 rounded-lg text-center font-bold text-glass bg-slate-800/60 border border-slate-600 focus:ring-2 focus:ring-slate-500 font-body"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const target = e.target as HTMLInputElement;
                    const pageNum = parseInt(target.value);
                    if (pageNum >= 1 && pageNum <= totalPages) {
                      setCurrentPage(pageNum);
                      target.value = '';
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
        </div>
      )}
      </div>

      {/* BULK ACTION BAR - Extracted to separate component */}
      <CollectionBulkActions
        isSelectionMode={isSelectionMode}
        selectedItems={selectedItems}
        setIsSelectionMode={setIsSelectionMode}
        setSelectedItems={setSelectedItems}
        paginatedItems={paginatedItems}
        sortedItems={sortedItems}
        onSelectAllPage={handleSelectAllPage}
        onSelectAllFiltered={handleSelectAllFiltered}
        onBulkToggle={handleBulkToggle}
        onBulkStorage={handleBulkStorage}
        onBulkPrice={handleBulkPrice}
        onBulkLine={handleBulkLine}
        onBulkCategory={handleBulkCategory}
        onShowChangeTypeModal={() => setShowChangeTypeModal(true)}
        onBulkExportCSV={handleBulkExportCSV}
        onShowMergeModal={() => setShowMergeModal(true)}
        onBulkDestash={handleBulkDestash}
        onBulkDelete={handleBulkDelete}
      />

      {/* ITEM DETAIL MODALS - Extracted to separate component */}
      <CollectionDetailModals
        selectedItem={selectedPolish}
        onClose={() => setSelectedPolish(null)}
        onUpdate={async (_id, updates) => {
          if (selectedPolish) {
            const updatedItem = { ...selectedPolish, ...updates };
            await handleSaveItem(updatedItem);
          }
        }}
        onDelete={handleDeleteItem}
        onRestore={handleRestoreItem}
        onPermanentDelete={handlePermanentDeleteItem}
        isInTrash={archiveFilter === 'trash'}
        onPrev={handleNavigatePrev}
        onNext={handleNavigateNext}
        hasPrev={hasPrevItem()}
        hasNext={hasNextItem()}
        existingBrands={uniqueBrands}
        userBrandCategories={brandCategories}
      />

      {/* ADD ITEM MODALS - Extracted to separate component */}
      <CollectionAddModals
        showAddModal={showAddModal}
        mainFilter={mainFilter}
        addItemCategory={addItemCategory}
        showItemPicker={showItemPicker}
        onCloseAddModal={() => setShowAddModal(false)}
        onCloseWithCategory={() => { setShowAddModal(false); setAddItemCategory(null); }}
        onItemAdded={handleItemAdded}
        onCloseItemPicker={() => setShowItemPicker(false)}
        onSelectCategory={(category) => {
          setShowItemPicker(false);
          setAddItemCategory(category);
          setShowAddModal(true);
        }}
        existingBrands={uniqueBrands}
        userBrandCategories={brandCategories}
      />

      {/* Merge Items Modal */}
      {showMergeModal && selectedItems.size >= 2 && (
      <MergeItemsModal
      isOpen={showMergeModal}
      onClose={() => setShowMergeModal(false)}
      items={allItems.filter(item => selectedItems.has(item.id))}
      userId={auth.currentUser?.uid || ''}
      onMergeComplete={(mergedItem, deletedIds) => {
        // Update local state instead of full reload
        setAllItems(prev => {
          // Remove deleted items and update the merged item
          const updated = prev.filter(item => !deletedIds.includes(item.id));
          // Find and update the merged item
          const mergedIndex = updated.findIndex(item => item.id === mergedItem.id);
          if (mergedIndex !== -1) {
            updated[mergedIndex] = { ...updated[mergedIndex], ...mergedItem };
          }
          return updated;
        });
        setSelectedItems(new Set());
        setIsSelectionMode(false);
      }}
      />
      )}

      {/* Change Item Type Modal */}
      {showChangeTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-glass font-heading">
                Change Item Type
              </h2>
              <button
                onClick={() => setShowChangeTypeModal(false)}
                className="p-2 hover:bg-slate-800/40 rounded-full transition-default"
              >
                <X className="icon-md text-glass-muted" />
              </button>
            </div>

            <p className="text-glass-secondary mb-6 font-body">
              Change <span className="font-bold text-glass-secondary">{selectedItems.size} item(s)</span> to a different type:
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleBulkChangeType('polish')}
                className="p-4 bg-pink-50 hover:bg-pink-100 border-2 border-pink-200 hover:border-pink-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">💅</span>
                <p className="font-bold text-glass mt-1">Nail Polish</p>
                <p className="text-sm text-glass-muted">Lacquer, gel, dip</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('plate')}
                className="p-4 bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 hover:border-purple-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">🔲</span>
                <p className="font-bold text-glass mt-1">Stamping Plate</p>
                <p className="text-sm text-glass-muted">Metal plates</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('transfer')}
                className="p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">🦋</span>
                <p className="font-bold text-glass mt-1">Transfer / Decal</p>
                <p className="text-sm text-glass-muted">Foils, wraps, decals</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('embellishment')}
                className="p-4 bg-amber-50 hover:bg-amber-100 border-2 border-amber-200 hover:border-amber-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">💎</span>
                <p className="font-bold text-glass mt-1">Embellishment</p>
                <p className="text-sm text-glass-muted">Gems, charms, studs</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('tool')}
                className="p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">🛠️</span>
                <p className="font-bold text-glass mt-1">Tool</p>
                <p className="text-sm text-glass-muted">Brushes, dotters, etc</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('powder')}
                className="p-4 bg-cyan-50 hover:bg-cyan-100 border-2 border-cyan-200 hover:border-cyan-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">✨</span>
                <p className="font-bold text-glass mt-1">Buffing Powder</p>
                <p className="text-sm text-glass-muted">Chrome, aurora, etc</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('acrylic')}
                className="p-4 bg-rose-50 hover:bg-rose-100 border-2 border-rose-200 hover:border-rose-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">💪</span>
                <p className="font-bold text-glass mt-1">Acrylic / Builder</p>
                <p className="text-sm text-glass-muted">Acrylics, builder gels, extensions</p>
              </button>

              <button
                onClick={() => handleBulkChangeType('cuticle_care')}
                className="p-4 bg-emerald-50 hover:bg-emerald-100 border-2 border-emerald-200 hover:border-emerald-400 rounded-xl transition-default text-left font-body"
              >
                <span className="text-2xl">💧</span>
                <p className="font-bold text-glass mt-1">Cuticle Care</p>
                <p className="text-sm text-glass-muted">Oils, creams, serums</p>
              </button>
            </div>

            <button
              onClick={() => setShowChangeTypeModal(false)}
              className="w-full mt-6 px-4 py-3 bg-slate-800/40 text-glass-secondary rounded-xl font-bold hover:bg-gray-200 transition-default font-body"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div 
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ease-out"
          style={{ animation: 'slideUp 0.3s ease-out' }}
        >
          <style>{`
            @keyframes slideUp {
              from { opacity: 0; transform: translateX(-50%) translateY(20px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg backdrop-blur-sm ${
            toast.type === 'success' ? 'bg-green-500/90 text-white' :
            toast.type === 'warning' ? 'bg-amber-500/90 text-white' :
            'bg-slate-700/90 text-white'
          }`}>
            <span className="font-medium font-body">{toast.message}</span>
            
            {toast.undoAction && (
              <button
                onClick={toast.undoAction}
                className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-bold transition-default"
              >
                Undo
              </button>
            )}
            
            <button
              onClick={dismissToast}
              className="p-1 hover:bg-white/20 rounded transition-default"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collection;
